import React, { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, Save } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import apiClient from "../api/axiosClient";
import { Button, Card, CardContent, Typography } from "@mui/material";
import SidebarGuideline from "./SidebarGuideline";
import "../styles/FormProduction.css";
import {downloadFile} from '../common'

// Constants for max lengths
const FIELD_MAX_LENGTH = 100;
const COMMENT_MAX_LENGTH = 300;
const PROFILE_MAX_LENGTH = 300;
const PHONE_MAX_LENGTH = 10;

// Validation regex
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRegex = /^\d{10}$/;

// Utility functions to initialize arrays

const initialSingleGAArray = (num = 5) => Array(num).fill("");
const initialDoubleGAArray = (num = 5) =>
  Array(num)
    .fill()
    .map(() => ["", ""]);

// Field definitions for single and double GA fields
const fieldDefs = {
  single: [
    "gaNames",
    "baseDate",
    "startWorkDate",
    "exclusivityPeriod",
    "forceMajeureStart",
    "forceMajeureEnd",
    "forceMajeureStart2",
    "forceMajeureEnd2",
  ],
  double: [
    "pngTotalMWP",
    "pngProRatedMWP",
    "pngActual",
    "pngBilled",
    "cngTotalMWP",
    "cngProRatedMWP",
    "cngActual",
    "pipelineTotalMWP",
    "pipelineProRatedMWP",
    "pipelineActual",
    "pngSalesVolume",
    "cngSalesVolume",
    "cbgIntake",
    "fatalities",
    "totalHoursWorked",
    "lostTimeInjuries",
    "totalRecordableIncident",
    "safetyAudits",
  ],
};

// Function to get initial form data
const getInitialFormData = (numGAs = 5) => ({
  Organisationname: "",
  category: "CGD Company of the Year",
  companyName: "",
  mailingAddress: "",
  authorityName: "",
  authorityTitle: "",
  authorityPhone: "",
  authorityLandline: "",
  authorityEmail: "",
  applicant_name: "",
  applicant_phone: "",
  applicant_email: "",
  applicant_name: "",
  applicant_phone: "",
  applicant_email: "",
  contact_name: "",
  contact_phone: "",
  contact_email: "",
  companyProfile: "",
  approvingAuthoritySignature: null,
  declaration: false,
  comment: "",
  baseDate: "2025-03-31",
  ga10YearMWP: ["", ""],
  ga8YearMWP: ["", ""],
  ga5YearMWP: ["", ""],
  ga3YearMWP: ["", ""],
  gaNoMWP: ["", ""],
  ...Object.fromEntries(
    fieldDefs.single.map((key) => [key, initialSingleGAArray(numGAs)])
  ),
  ...Object.fromEntries(
    fieldDefs.double.map((key) => [key, initialDoubleGAArray(numGAs)])
  ),
  // gaNames: Array(numGAs).fill(""),
  attachments1: { description: "", file: null },
  attachments2: { description: "", file: null },
  attachments3: { description: "", file: null },
  attachments4: { description: "", file: null },
});

// Field definitions for parts 1 and 2
const part1 = [
  //  ["1", "General Information", "", null, false, true], // header row
  ["1.1", "Number of GAs with 10 year MWP Target", "No.", "ga10YearMWP", true],
  ["1.2", "Number of GAs with 8 year MWP Target", "No.", "ga8YearMWP", true],
  ["1.3", "Number of GAs with 5 year MWP Target", "No.", "ga5YearMWP", true],
  ["1.4", "Number of GAs with 3 year MWP Target", "No.", "ga3YearMWP", true],
  ["1.5", "Number of GAs with no MWP Target", "No.", "gaNoMWP", true],

  // ["2", "GA Specific Information", "", null, false, true],
  ["2.1", "Base date for evaluation", "Date", "baseDate", true],
  [
    "2.2",
    "Effective start of work date (Contract Year)",
    "Date",
    "startWorkDate",
    true,
  ],
  [
    "2.3",
    "Exclusivity Period - Exclusivity from the purview of common carrier or contract carrier",
    "Years",
    "exclusivityPeriod",
    true,
  ],
  [
    "2.4",
    "Force Majeure start date (if any)",
    "Date",
    "forceMajeureStart",
    true,
  ],
  [
    "2.5",
    "Force Majeure end date (if any) (Note: if FM extension has been provided more than once, please provide data for all of them)",
    "Date",
    "forceMajeureEnd",
    true,
  ],
  [
    "2.6",
    "Force Majeure start date (if any)",
    "Date",
    "forceMajeureStart2",
    true,
  ],
  [
    "2.7",
    "Force Majeure end date (if any) (Note: if FM extension has been provided more than once, please provide data for all of them)",
    "Date",
    "forceMajeureEnd2",
    true,
  ],

  ["3", "D-PNG Connection", ""],
  ["3.1", "Total MWP Target", "No.", "pngTotalMWP", true],
  [
    "3.2",
    "Pro-rated MWP Target (Total PNG connections expected based on pro-rata schedule)",
    "No.",
    "pngProRatedMWP",
    true,
  ],
  ["3.3", "Actual PNG connections achieved", "No.", "pngActual", true],
  [
    "3.4",
    "Number of Billed connections in 2024-25 as reported to PNGRB (Cumulative)",
    "No.",
    "pngBilled",
    true,
  ],
];

const part2 = [
  // ["4", "CNG Stations", "", null, false, true],
  ["4.1", "Total MWP Target", "No.", "cngTotalMWP", true],
  ["4.2", "Pro-rated MWP Target ", "No.", "cngProRatedMWP", true],
  ["4.3", "Actual no. of CNG stations achieved", "No.", "cngActual", true],
  // [
  //   "4.4",
  //   "Weighted Average Effective age of the company",
  //   "No.",
  //   "cngActualTotal",
  //   false,
  // ],
  // ["5", "Inch-km of Pipeline Laid", "", null, false, true],
  ["5.1", "Total MWP Target ", "No.", "pipelineTotalMWP", true],
  [
    "5.2",
    "Pro-rated MWP Target (Total inch km of steel + MDPE pipeline) expected based on pro-rata schedule)",
    "No.",
    "pipelineProRatedMWP",
    true,
  ],
  [
    "5.3",
    "Actual inch-km of steel+ MDPE pipeline laid",
    "No.",
    "pipelineActual",
    true,
  ],
  // [
  //   "5.4",
  //   "Weighted Average Effective age of the company",
  //   "No.",
  //   "pipelineActualTotal",
  //   false,
  // ],
  // ["6", "Natural Gas Sales & CBG Intake", "", null, false, true],
  ["6.1", "PNG Sales Volume", "MMSCM", "pngSalesVolume", true],
  ["6.2", "CNG Sales Volume", "MMSCM", "cngSalesVolume", true],
  ["6.3", "CBG intake in CGD network", "MMSCM", "cbgIntake", true],
  // ["7", "Safety", "", null, false, true],
  ["7.1", "Number of Fatalities ", "", "fatalities", true],
  ["7.2", "Total Hours Worked ", "", "totalHoursWorked", true],
  ["7.3", "Lost Time Injuries", "", "lostTimeInjuries", true],
  ["7.4", "Total Recordable Incidents ", "", "totalRecordableIncident", true],
  [
    "7.5",
    "Number of safety audits conducted in 2024-25",
    "",
    "safetyAudits",
    true,
  ],
];

const RegistrationCGD = () => {
  const [userInfo, setUserInfo] = useState();
  const [formId, setFormId] = useState("Unknown");
  const [PkId, setPkId] = useState(0);

  const [numGAs, setNumGAs] = useState(1);
  const [formData, setFormData] = useState(getInitialFormData(5));
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [activeItem, setActiveItem] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [copyApplicantData, setCopyApplicantData] = useState(false);

  const location = useLocation();
  const awardTitle = location.state?.awardTitle || "CGD Company of the Year";

  // State for calculated rows
  const [calculatedRows, setCalculatedRows] = useState([
    { key: "exclusivityPeriodTotal", value: 0 },
    { key: "pngActualTotal", value: 0 },
    { key: "cngActualTotal", value: 0 },
    { key: "pipelineActualTotal", value: 0 },
  ]);

  useEffect(() => {
    if (userInfo && formId) {
      prefillFormData();
    } else {
      console.warn(
        `userInfo OR formId is invalid; Kindly check the method. and data`
      );
    }
  }, [userInfo, formId]);

  const isSubmited = () => formData.form_mode === "submit";

  // --- helpers (include in same file) ---
  function parsePossibleArray(value) {
    // Accepts: array, JSON string, comma-separated string, single value
    if (value === null || typeof value === "undefined" || value === "")
      return [];
    if (Array.isArray(value)) return value;
    if (typeof value === "object") return value;
    if (typeof value === "string") {
      const trimmed = value.trim();
      // JSON array/object string
      if (
        (trimmed.startsWith("[") && trimmed.endsWith("]")) ||
        (trimmed.startsWith("{") && trimmed.endsWith("}"))
      ) {
        try {
          const parsed = JSON.parse(trimmed);
          return Array.isArray(parsed) ? parsed : [parsed];
        } catch (e) {
          // fallthrough to CSV parse
        }
      }
      // comma separated fallback
      if (trimmed.includes(",")) return trimmed.split(",").map((s) => s.trim());
      // single value -> single-element array
      return [trimmed];
    }
    return [];
  }

  // When gaRows (in formData) changes, ensure numGAs and per-GA arrays are sized and available
useEffect(() => {
  const gaRows = formData.gaRows || [];
  const detected = Array.isArray(gaRows) ? gaRows.length : 0;
  if (typeof setNumGAs === "function") {
    setNumGAs(Math.max(detected, 1)); // keep at least 1 row in UI if you want
  }

  // ensure per-GA double arrays exist and match length
  const ensureArrayOfPairs = (key) => {
    const current = formData[key];
    const len = detected;
    if (!Array.isArray(current) || current.length !== len) {
      // If current is flat array with len*2 values, chunk it
      if (Array.isArray(current) && len > 0 && current.length === len * 2) {
        // chunk into pairs
        const chunked = [];
        for (let i = 0; i < current.length; i += 2) chunked.push([current[i] ?? "", current[i+1] ?? ""]);
        setFormData(prev => ({ ...prev, [key]: chunked }));
        return;
      }
      // if it's already array-of-arrays but wrong size, resize
      if (Array.isArray(current) && Array.isArray(current[0])) {
        const resized = Array.from({length: len}, (_, i) => current[i] || ["",""]);
        setFormData(prev => ({ ...prev, [key]: resized }));
        return;
      }
      // default initialize empty pair list
      const blank = Array.from({length: len}, () => ["", ""]);
      setFormData(prev => ({ ...prev, [key]: blank }));
    }
  };

  // Keys used in your form that are per-GA double arrays:
  ["pngActual","cngActual","pipelineActual","pngTotalMWP","pngProRatedMWP","pngBilled","cngTotalMWP","cngProRatedMWP","pipelineTotalMWP","pipelineProRatedMWP"].forEach(ensureArrayOfPairs);

  // Also ensure singular arrays like gaNames, baseDate etc match
  const ensureArrayLength = (key) => {
    const arr = formData[key];
    if (!Array.isArray(arr) || arr.length !== detected) {
      const newArr = Array.from({length: detected}, (_, i) => ( (arr && arr[i] !== undefined) ? arr[i] : "" ));
      setFormData(prev => ({ ...prev, [key]: newArr }));
    }
  };
  ["gaNames","baseDate","startWorkDate","exclusivityPeriod","forceMajeureStart","forceMajeureEnd","ga10YearMWP","ga8YearMWP","ga5YearMWP","ga3YearMWP","gaNoMWP"].forEach(ensureArrayLength);

}, [formData.gaRows]); // run when gaRows changes



  function safeStr(v) {
    if (v === null || typeof v === "undefined") return "";
    if (typeof v === "object") return v;
    return String(v);
  }

async function prefillFormData() {
  if (!formId) {
    console.warn("prefillFormData: missing formId");
    return false;
  }

  const url =
    typeof userInfo !== "undefined" && userInfo && userInfo.id
      ? `/registration-cgd/?userid=${encodeURIComponent(userInfo.id)}&form_id=${encodeURIComponent(formId)}`
      : `/registration-cgd/?form_id=${encodeURIComponent(formId)}`;

  // helpers
  const safeStr = (v) => (v === null || typeof v === "undefined" ? "" : (typeof v === "object" ? v : String(v)));
  const parsePossibleArray = (value) => {
    if (value === null || typeof value === "undefined" || value === "") return [];
    if (Array.isArray(value)) return value;
    if (typeof value === "object") return value;
    if (typeof value === "string") {
      const trimmed = value.trim();
      // JSON array/object string
      if ((trimmed.startsWith("[") && trimmed.endsWith("]")) || (trimmed.startsWith("{") && trimmed.endsWith("}"))) {
        try {
          const parsed = JSON.parse(trimmed);
          return Array.isArray(parsed) ? parsed : [parsed];
        } catch (e) {
          // fallthrough to CSV parse
        }
      }
      // comma separated fallback
      if (trimmed.includes(",")) return trimmed.split(",").map((s) => s.trim());
      // single value -> single-element array
      return [trimmed];
    }
    return [];
  };

  const getFrom = (obj, ...keys) => {
    for (const k of keys) {
      if (obj[k] !== undefined && obj[k] !== null) return obj[k];
      // snake_case variant
      const snake = (k || "").replace(/[A-Z]/g, (m) => "_" + m.toLowerCase());
      if (obj[snake] !== undefined && obj[snake] !== null) return obj[snake];
      // camelCase variant
      const camel = (k || "").replace(/[_-]([a-z])/g, (_, c) => c.toUpperCase());
      if (obj[camel] !== undefined && obj[camel] !== null) return obj[camel];
    }
    return undefined;
  };

  const chunkArray = (arr, size) => {
    if (!Array.isArray(arr)) return [];
    const out = [];
    for (let i = 0; i < arr.length; i += size) {
      out.push(arr.slice(i, i + size));
    }
    return out;
  };

  try {
    const res = await apiClient.get(url);
    if (!res || (res.status && res.status >= 400)) {
      console.warn("prefillFormData: no data or error response", res);
      return false;
    }

    // Normalize payload (array or single object)
    let items = Array.isArray(res.data) ? res.data.slice() : [res.data];
    if (!items || items.length === 0) {
      console.log("prefillFormData: no items", res.data);
      return false;
    }

    // If multiple items, pick most recent by updated_at/created_at or highest id
    if (items.length > 1) {
      items.sort((a, b) => {
        const aDate = new Date(getFrom(a, "updated_at", "updatedAt", "created_at", "createdAt") || 0).getTime();
        const bDate = new Date(getFrom(b, "updated_at", "updatedAt", "created_at", "createdAt") || 0).getTime();
        if (bDate !== aDate) return bDate - aDate;
        return (b.id || 0) - (a.id || 0);
      });
    }

    const _ResData = items[0];
    const pk = _ResData.id ?? _ResData.pk ?? 0;
    setPkId(pk);

    // Base scalar fields
    const newFields = {
      Organisationname: safeStr(getFrom(_ResData, "Organisation_name", "organisation_name", "organisationName", "company_name")),
      category: safeStr(getFrom(_ResData, "category")),
      mailingAddress: safeStr(getFrom(_ResData, "mailing_address", "mailingAddress", "address")),
      authorityName: safeStr(getFrom(_ResData, "authority_name", "authorityName", "approving_authority_name")),
      authorityTitle: safeStr(getFrom(_ResData, "authority_title", "authorityTitle", "approving_authority_title")),
      authorityPhone: safeStr(getFrom(_ResData, "authority_phone", "authorityPhone", "approving_authority_phone")),
      authorityLandline: safeStr(getFrom(_ResData, "authority_landline", "authorityLandline")),
      authorityEmail: safeStr(getFrom(_ResData, "authority_email", "authorityEmail")),
      applicant_name: safeStr(getFrom(_ResData, "applicant_name", "applicantName", "contact_name")),
      applicant_phone: safeStr(getFrom(_ResData, "applicant_phone", "applicantPhone", "contact_phone")),
      applicant_email: safeStr(getFrom(_ResData, "applicant_email", "applicantEmail", "contact_email")),
      contact_name: safeStr(getFrom(_ResData, "contact_name", "contactName")),
      contact_phone: safeStr(getFrom(_ResData, "contact_phone", "contactPhone")),
      contact_email: safeStr(getFrom(_ResData, "contact_email", "contactEmail")),
      companyProfile: safeStr(getFrom(_ResData, "company_profile", "companyProfile", "company_description")),
      comment: safeStr(getFrom(_ResData, "comment", "notes", "remarks")),
      declaration:
        (getFrom(_ResData, "declaration") === true ||
          getFrom(_ResData, "declaration") === "true" ||
          getFrom(_ResData, "declaration") === 1) || false,
      form_mode: getFrom(_ResData, "form_mode", "formMode") ?? "draft",
    };

    // --- GA rows detection: try many possible names & shapes ---
    let gaObjects = null;
    const gaArrayCandidates = [
      "ga_set", "ga_records", "ga_rows", "ga_items", "ga_objects",
      "gas", "gaset", "gaList", "ga_list", "gaObjects", "gnztechnology_set",
      "related_ga", "ga_rows", "gasset", "gases"
    ];
    for (const name of gaArrayCandidates) {
      if (Array.isArray(_ResData[name]) && _ResData[name].length > 0) {
        gaObjects = _ResData[name];
        break;
      }
    }

    // fallback: find any array-of-objects that looks like GA rows
    if (!gaObjects) {
      for (const key of Object.keys(_ResData)) {
        if (Array.isArray(_ResData[key]) && _ResData[key].length > 0 && typeof _ResData[key][0] === "object") {
          const sample = _ResData[key][0];
          if (["name", "ga_name", "baseDate", "startWorkDate", "exclusivityPeriod"].some(x => Object.prototype.hasOwnProperty.call(sample, x))) {
            gaObjects = _ResData[key];
            break;
          }
        }
      }
    }

    // If no object-array found, attempt to build GA objects from parallel arrays in the response
    const perFieldCandidates = {
      gaNames: ["gaNames", "ga_names", "ganames", "ga_name_list", "ga_name"],
      baseDate: ["baseDate", "base_date", "ga_base_date"],
      startWorkDate: ["startWorkDate", "start_work_date", "ga_start_date"],
      exclusivityPeriod: ["exclusivityPeriod", "exclusivity_period", "exclusivity"],
      forceMajeureStart: ["forceMajeureStart", "force_majeure_start", "fm_start"],
      forceMajeureEnd: ["forceMajeureEnd", "force_majeure_end", "fm_end"],
      forceMajeureStart2: ["forceMajeureStart2", "force_majeure_start2"],
      forceMajeureEnd2: ["forceMajeureEnd2", "force_majeure_end2"],
      ga10YearMWP: ["ga10YearMWP", "ga_10_year_mwp"],
      ga8YearMWP: ["ga8YearMWP", "ga_8_year_mwp"],
      ga5YearMWP: ["ga5YearMWP", "ga_5_year_mwp"],
      ga3YearMWP: ["ga3YearMWP", "ga_3_year_mwp"],
      gaNoMWP: ["gaNoMWP", "ga_no_mwp"],
    };

    const perFieldArrays = {};
    let anyPerFieldFound = false;
    for (const [localKey, candidates] of Object.entries(perFieldCandidates)) {
      let arr = null;
      for (const cand of candidates) {
        const raw = getFrom(_ResData, cand);
        if (raw !== undefined && raw !== null) {
          arr = parsePossibleArray(raw);
          break;
        }
      }
      if (arr && arr.length > 0) {
        perFieldArrays[localKey] = arr;
        anyPerFieldFound = true;
      } else {
        perFieldArrays[localKey] = [];
      }
    }

    if (!gaObjects && anyPerFieldFound) {
      const maxLen = Math.max(...Object.values(perFieldArrays).map(a => (Array.isArray(a) ? a.length : 0)), 0);
      gaObjects = [];
      for (let i = 0; i < maxLen; i++) {
        gaObjects.push({
          name: perFieldArrays.gaNames[i] ?? "",
          baseDate: perFieldArrays.baseDate[i] ?? "",
          startWorkDate: perFieldArrays.startWorkDate[i] ?? "",
          exclusivityPeriod: perFieldArrays.exclusivityPeriod[i] ?? "",
          forceMajeureStart: perFieldArrays.forceMajeureStart[i] ?? "",
          forceMajeureEnd: perFieldArrays.forceMajeureEnd[i] ?? "",
          forceMajeureStart2: perFieldArrays.forceMajeureStart2[i] ?? "",
          forceMajeureEnd2: perFieldArrays.forceMajeureEnd2[i] ?? "",
          ga10YearMWP: perFieldArrays.ga10YearMWP[i] ?? "",
          ga8YearMWP: perFieldArrays.ga8YearMWP[i] ?? "",
          ga5YearMWP: perFieldArrays.ga5YearMWP[i] ?? "",
          ga3YearMWP: perFieldArrays.ga3YearMWP[i] ?? "",
          gaNoMWP: perFieldArrays.gaNoMWP[i] ?? "",
        });
      }
    }

    // Normalize gaRows into array of objects
    const gaRows = Array.isArray(gaObjects)
      ? gaObjects.map(g => ({
          name: getFrom(g, "name", "ga_name", "gaName", "ganame", "name_text") ?? "",
          baseDate: getFrom(g, "baseDate", "base_date", "date_base") ?? "",
          startWorkDate: getFrom(g, "startWorkDate", "start_work_date", "start_date") ?? "",
          exclusivityPeriod: getFrom(g, "exclusivityPeriod", "exclusivity_period") ?? "",
          forceMajeureStart: getFrom(g, "forceMajeureStart", "force_majeure_start", "fm_start") ?? "",
          forceMajeureEnd: getFrom(g, "forceMajeureEnd", "force_majeure_end", "fm_end") ?? "",
          forceMajeureStart2: getFrom(g, "forceMajeureStart2", "force_majeure_start2") ?? "",
          forceMajeureEnd2: getFrom(g, "forceMajeureEnd2", "force_majeure_end2") ?? "",
          ga10YearMWP: getFrom(g, "ga10YearMWP", "ga_10_year_mwp") ?? "",
          ga8YearMWP: getFrom(g, "ga8YearMWP", "ga_8_year_mwp") ?? "",
          ga5YearMWP: getFrom(g, "ga5YearMWP", "ga_5_year_mwp") ?? "",
          ga3YearMWP: getFrom(g, "ga3YearMWP", "ga_3_year_mwp") ?? "",
          gaNoMWP: getFrom(g, "gaNoMWP", "ga_no_mwp") ?? "",
          __raw: g
      })) : [];

    newFields.gaRows = gaRows;
    newFields.gaNames = gaRows.map(r => safeStr(r.name));
    newFields.baseDate = gaRows.map(r => safeStr(r.baseDate));
    newFields.startWorkDate = gaRows.map(r => safeStr(r.startWorkDate));
    newFields.exclusivityPeriod = gaRows.map(r => safeStr(r.exclusivityPeriod));
    newFields.forceMajeureStart = gaRows.map(r => safeStr(r.forceMajeureStart));
    newFields.forceMajeureEnd = gaRows.map(r => safeStr(r.forceMajeureEnd));
    newFields.forceMajeureStart2 = gaRows.map(r => safeStr(r.forceMajeureStart2));
    newFields.forceMajeureEnd2 = gaRows.map(r => safeStr(r.forceMajeureEnd2));
    newFields.ga10YearMWP = gaRows.map(r => safeStr(r.ga10YearMWP));
    newFields.ga8YearMWP = gaRows.map(r => safeStr(r.ga8YearMWP));
    newFields.ga5YearMWP = gaRows.map(r => safeStr(r.ga5YearMWP));
    newFields.ga3YearMWP = gaRows.map(r => safeStr(r.ga3YearMWP));
    newFields.gaNoMWP = gaRows.map(r => safeStr(r.gaNoMWP));

    // --- Numeric / multi-value arrays (try many variants) ---
    const arrayKeys = [
      "pngTotalMWP","pngProRatedMWP","pngActual","pngBilled",
      "cngTotalMWP","cngProRatedMWP","cngActual",
      "pipelineTotalMWP","pipelineProRatedMWP","pipelineActual",
      "pngSalesVolume","cngSalesVolume","cbgIntake",
      "fatalities","totalHoursWorked","lostTimeInjuries",
      "totalRecordableIncident","safetyAudits"
    ];

    // read and parse arrays from response
    arrayKeys.forEach((k) => {
      const snake = k.replace(/[A-Z]/g, (m) => "_" + m.toLowerCase());
      const rawVal = getFrom(_ResData, k, k.toLowerCase(), snake, snake + "_list");
      newFields[k] = parsePossibleArray(rawVal);
    });

    // --- Normalize multi-value arrays into shapes expected by UI ---
    const numDetectedGAs = (newFields.gaRows || []).length || 0;

    // keys that may be single-value-per-GA or nested array per GA
    const keysThatAreSingleOrNested = [
      "pngTotalMWP","cngTotalMWP","pipelineTotalMWP",
      "pngProRatedMWP","cngProRatedMWP","pipelineProRatedMWP",
      "pngSalesVolume","cngSalesVolume","cbgIntake"
    ];

    // keys that often contain two-values-per-GA or nested arrays
    const keysThatArePairsOrNested = [
      "pngActual","cngActual","pipelineActual","pngBilled"
    ];

    const normalizeKey = (k, treatAsPairs = false) => {
      const val = newFields[k] ?? [];
      if (!Array.isArray(val)) {
        newFields[k] = [];
        return;
      }
      // already nested arrays? keep but maybe resize
      if (val.length > 0 && Array.isArray(val[0])) {
        // if nested arrays but length doesn't match ga count, resize/pad/truncate
        if (numDetectedGAs > 0 && val.length !== numDetectedGAs) {
          const resized = Array.from({length: numDetectedGAs}, (_,i) => val[i] ?? (Array.isArray(val[0]) ? Array.from({length: val[0].length}, ()=>"") : ""));
          newFields[k] = resized;
        }
        return;
      }
      // flat array case
      const flat = val;
      if (numDetectedGAs > 0) {
        if (flat.length === numDetectedGAs) {
          // one value per GA -> keep as simple array
          newFields[k] = flat.map(v => (v === null || typeof v === "undefined" ? "" : v));
          return;
        }
        if (flat.length === numDetectedGAs * 2) {
          // probably two values per GA -> chunk into pairs
          newFields[k] = chunkArray(flat, 2).map(arr => arr.map(v => (v === null || typeof v === "undefined" ? "" : v)));
          return;
        }
        if (flat.length % numDetectedGAs === 0) {
          // plausible grouped flat array: split into 'groupSize' chunks per GA
          const groupSize = flat.length / numDetectedGAs;
          newFields[k] = chunkArray(flat, groupSize).map(chunk => chunk.map(v => (v === null || typeof v === "undefined" ? "" : v)));
          return;
        }
      }
      // fallback: keep flat values if nothing else fits
      newFields[k] = flat.map(v => (v === null || typeof v === "undefined" ? "" : v));
    };

    keysThatAreSingleOrNested.forEach(k => normalizeKey(k, false));
    keysThatArePairsOrNested.forEach(k => normalizeKey(k, true));

    // Ensure the most important arrays exist in some reasonable shape for controlled inputs:
    if (!Array.isArray(newFields.pngTotalMWP)) newFields.pngTotalMWP = Array.from({length: Math.max(numDetectedGAs,1)}, ()=>"");
    if (!Array.isArray(newFields.cngTotalMWP)) newFields.cngTotalMWP = Array.from({length: Math.max(numDetectedGAs,1)}, ()=>"");
    if (!Array.isArray(newFields.pipelineTotalMWP)) newFields.pipelineTotalMWP = Array.from({length: Math.max(numDetectedGAs,1)}, ()=>"");
    if (!Array.isArray(newFields.pngActual)) newFields.pngActual = Array.from({length: Math.max(numDetectedGAs,1)}, ()=> ["",""]);
    if (!Array.isArray(newFields.cngActual)) newFields.cngActual = Array.from({length: Math.max(numDetectedGAs,1)}, ()=> ["",""]);
    if (!Array.isArray(newFields.pipelineActual)) newFields.pipelineActual = Array.from({length: Math.max(numDetectedGAs,1)}, ()=> ["",""]);

    // Attachments 1..4 -> keep url & desc, file = null
    for (let i = 1; i <= 4; i++) {
      const desc = getFrom(_ResData, `attachments${i}_desc`, `attachments${i}Desc`, `attachment${i}_desc`) ?? "";
      let urlVal = getFrom(_ResData, `attachments${i}`, `attachments${i}_url`, `attachments${i}_file`);
      if (urlVal && typeof urlVal === "object" && urlVal.url) urlVal = urlVal.url;
      newFields[`attachments${i}`] = { description: safeStr(desc), file: null, url: urlVal ?? null };
    }

    // signature preview url
    let sig = getFrom(_ResData, "approving_authority_file", "approving_authority_file_url", "approvingAuthoritySignatureUrl");
    if (sig && typeof sig === "object" && sig.url) sig = sig.url;
    newFields.approvingAuthoritySignature = null;
    newFields.approvingAuthoritySignatureUrl = sig ?? null;

    // Final safety: ensure arrays exist for keys used by UI
    if (!Array.isArray(newFields.gaRows)) newFields.gaRows = [];
    if (!Array.isArray(newFields.gaNames)) newFields.gaNames = newFields.gaRows.map(r => safeStr(r.name));
    // ensure forceMajeureStart/End arrays match ga count
    newFields.forceMajeureStart = newFields.forceMajeureStart ?? newFields.gaRows.map(r => safeStr(r.forceMajeureStart));
    newFields.forceMajeureEnd = newFields.forceMajeureEnd ?? newFields.gaRows.map(r => safeStr(r.forceMajeureEnd));
    newFields.forceMajeureStart2 = newFields.forceMajeureStart2 ?? newFields.gaRows.map(r => safeStr(r.forceMajeureStart2));
    newFields.forceMajeureEnd2 = newFields.forceMajeureEnd2 ?? newFields.gaRows.map(r => safeStr(r.forceMajeureEnd2));

    // Write normalized data to state
    setFormData(prev => ({ ...prev, ...newFields }));

    // Update numGAs so UI that uses this grows automatically (if you have setNumGAs)
    if (typeof setNumGAs === "function") {
      setNumGAs(Math.max((newFields.gaRows || []).length, 1));
    }

    console.log("prefillFormData: applied, pk:", pk, "gaRows length:", (newFields.gaRows || []).length);
    return true;
  } catch (err) {
    console.error("prefillFormData error:", err);
    return false;
  }
}



  useEffect(() => {
    [...part1, ...part2].forEach(([_, __, ___, key, isInput]) => {
      if (!key || !isInput) return;
      if (!formData[key]) {
        // For GA-specific data with two years per GA
        formData[key] = Array.from({ length: numGAs }, () => ["", ""]);
      }
    });
    setFormData({ ...formData });
  }, [numGAs]);

  // Calculate totals for calculated rows
  useEffect(() => {
    const exclusivityTotal = formData.exclusivityPeriod
      .map((val) => parseFloat(val) || 0)
      .reduce((sum, val) => sum + val, 0);

    const pngTotal = formData.pngActual
      .flat()
      .map((val) => parseFloat(val) || 0)
      .reduce((sum, val) => sum + val, 0);

    const cngTotal = formData.cngActual
      .flat()
      .map((val) => parseFloat(val) || 0)
      .reduce((sum, val) => sum + val, 0);

    const pipelineTotal = formData.pipelineActual
      .flat()
      .map((val) => parseFloat(val) || 0)
      .reduce((sum, val) => sum + val, 0);

    setCalculatedRows([
      { key: "exclusivityPeriodTotal", value: exclusivityTotal },
      { key: "pngActualTotal", value: pngTotal },
      { key: "cngActualTotal", value: cngTotal },
      { key: "pipelineActualTotal", value: pipelineTotal },
    ]);
  }, [
    formData.exclusivityPeriod,
    formData.pngActual,
    formData.cngActual,
    formData.pipelineActual,
  ]);

  const clearFieldError = useCallback((field) => {
    setFieldErrors((prev) => {
      if (prev[field]) {
        const { [field]: omitted, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, []);

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem("user_info");
      if (stored) {
        const user = JSON.parse(stored);
        setUserInfo(JSON.parse(stored));
        setFormId(`CGD_Registration_${user.id}`);
        setFormData((prev) => ({
          ...prev,
          userid: user.id || "",
          firstname: user.first_name || "",
          lastname: user.last_name || "",
          company_name: user.organisation_name || "",
          Organisationname: user.organisation_name || "", // prefill organisation_name field
          // Fill applicant_* only (we DON'T prefill contact_* here)
          applicant_name: user.applicant_name || "",
          applicant_phone: user.applicant_phone || "",
          applicant_email: user.email || "",
        }));
      }
    } catch (err) {
      console.warn("Failed to parse user_info from localStorage:", err);
    }
  }, []);

  //    useEffect(() => {
  //   try {
  //     const stored = sessionStorage.getItem("user_info");
  //     if (stored) {
  //       const user = JSON.parse(stored);
  //       setUserInfo(JSON.parse(stored));
  //       setFormId(`CGD_Registration_${user.id}`);
  //       setFormData((prev) => ({
  //         ...prev,
  //         userid: user.id || "",
  //         firstname: user.first_name || "",
  //         lastname: user.last_name || "",
  //         company_name: user.Organisation_name || "",
  //         Organisationname: user.Organisation_name || "", // prefill organisation_name field
  //         // Fill applicant_* only (we DON'T prefill contact_* here)
  //         applicant_name: user.applicant_name || "",
  //         applicant_phone: user.applicant_phone || "",
  //         applicant_email: user.email || "",
  //       }));
  //     }
  //   } catch (err) {
  //     console.warn("Failed to parse user_info from localStorage:", err);
  //   }
  // }, []);
  // ====== Toggle handler: copy from applicant (or localStorage fallback) ======
  const handleCopyApplicantToggle = (e) => {
    const checked = e.target.checked;
    // setCopyApplicantData(checked);

    const model = {
      contact_name: "",
      contact_phone: "",
      contact_email: "",
    };

    if (checked) {
      const userInfo = JSON.parse(sessionStorage.getItem("user_info"));
      if (userInfo) {
        model.contact_name = userInfo.applicant_name;
        model.contact_phone = userInfo.applicant_phone;
        model.contact_email = userInfo.email;
      }
    }

    setFormData((prev) => ({ ...prev, ...model }));

    clearFieldError("contact_name");
    clearFieldError("contact_phone");
    clearFieldError("contact_email");
  };

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // --- add near other useState hooks ---
  // const [showDeleteModal, setShowDeleteModal] = useState(false);

  // --- open delete-confirmation for last GA ---
  const confirmDeleteLastGA = () => {
    if (numGAs <= 1) return; // protect last GA
    setShowDeleteModal(true);
  };

  // --- delete last GA when user confirms ---
  const handleDeleteLastGA = () => {
    if (numGAs <= 1) {
      setShowDeleteModal(false);
      return;
    }
    setFormData((prev) => {
      const newFormData = { ...prev };

      // remove last element from arrays safely
      newFormData.gaNames = (prev.gaNames || []).slice(0, -1);

      fieldDefs.single.forEach((field) => {
        newFormData[field] = (prev[field] || []).slice(0, -1);
      });

      fieldDefs.double.forEach((field) => {
        newFormData[field] = (prev[field] || []).slice(0, -1);
      });

      [
        "ga10YearMWP",
        "ga8YearMWP",
        "ga5YearMWP",
        "ga3YearMWP",
        "gaNoMWP",
      ].forEach((k) => {
        newFormData[k] = (prev[k] || []).slice(0, -1);
      });

      return newFormData;
    });

    setNumGAs((prev) => Math.max(1, prev - 1));
    setShowDeleteModal(false);
  };

  // --- cancel deletion ---
  const cancelDeleteLastGA = () => {
    setShowDeleteModal(false);
  };

  // Keep arrays present when numGAs changes
  useEffect(() => {
    setFormData((prev) => {
      const copy = { ...prev };
      // ensure gaNames exists
      if (!Array.isArray(copy.gaNames) || copy.gaNames.length < numGAs) {
        copy.gaNames = [
          ...(copy.gaNames || []),
          ...Array(numGAs - (copy.gaNames?.length || 0)).fill(""),
        ];
      } else if (copy.gaNames.length > numGAs) {
        copy.gaNames = copy.gaNames.slice(0, numGAs);
      }

      fieldDefs.single.forEach((key) => {
        if (!Array.isArray(copy[key]) || copy[key].length < numGAs) {
          copy[key] = [
            ...(copy[key] || []),
            ...Array(numGAs - (copy[key]?.length || 0)).fill(""),
          ];
        } else if (copy[key].length > numGAs) {
          copy[key] = copy[key].slice(0, numGAs);
        }
      });

      fieldDefs.double.forEach((key) => {
        if (!Array.isArray(copy[key]) || copy[key].length < numGAs) {
          copy[key] = [
            ...(copy[key] || []),
            ...Array(numGAs - (copy[key]?.length || 0)).fill(["", ""]),
          ];
        } else if (copy[key].length > numGAs) {
          copy[key] = copy[key].slice(0, numGAs);
        }
      });

      // also for gaXYear arrays
      [
        "ga10YearMWP",
        "ga8YearMWP",
        "ga5YearMWP",
        "ga3YearMWP",
        "gaNoMWP",
      ].forEach((k) => {
        if (!Array.isArray(copy[k]) || copy[k].length < numGAs) {
          copy[k] = [
            ...(copy[k] || []),
            ...Array(numGAs - (copy[k]?.length || 0)).fill(""),
          ];
        } else if (copy[k].length > numGAs) {
          copy[k] = copy[k].slice(0, numGAs);
        }
      });

      return copy;
    });
  }, [numGAs]);

  const addMoreGA = () => {
    setNumGAs((prev) => prev + 1);
    setFormData((prev) => {
      const newFormData = { ...prev };

      // default name for new GA: use GA1 value if present, otherwise empty
      const defaultName =
        prev.gaNames && prev.gaNames[0] ? prev.gaNames[0] : "";

      newFormData.gaNames = [...(prev.gaNames || []), defaultName];

      // add slots for single fields (one value per GA)
      fieldDefs.single.forEach((field) => {
        newFormData[field] = [...(prev[field] || []), ""];
      });

      // add slots for double fields (two-year values per GA)
      fieldDefs.double.forEach((field) => {
        newFormData[field] = [...(prev[field] || []), ["", ""]];
      });

      // add for the MWP arrays
      [
        "ga10YearMWP",
        "ga8YearMWP",
        "ga5YearMWP",
        "ga3YearMWP",
        "gaNoMWP",
      ].forEach((k) => {
        newFormData[k] = [...(prev[k] || []), ""];
      });

      return newFormData;
    });
  };

  // const areFieldsEmpty3 = (start, end) => {
  //   const flattenedItems = [];

  //   // Recursive extractor to handle nested subItems and top-level keys
  //   const extractItems = (items) => {
  //     for (const item of items) {
  //       if (item[3]) {
  //         // if key exists
  //         flattenedItems.push({ num: parseFloat(item[0]), key: item[3] });
  //       }
  //     }
  //   };

  //   extractItems(part1);

  //   // Filter by the given range
  //   const filteredItems = flattenedItems.filter(
  //     (item) => item.num >= start && item.num <= end
  //   );

  //   // Check each field’s value
  //   for (const item of filteredItems) {
  //     const val = formData[item.key];

  //     if (val === undefined || val === null || val === "") {
  //       return true;
  //     }

  //     if (
  //       Array.isArray(val) &&
  //       val.some(
  //         (v) =>
  //           v === "" ||
  //           v === null ||
  //           v === undefined ||
  //           (Array.isArray(v) &&
  //             v.some((sub) => sub === "" || sub === null || sub === undefined))
  //       )
  //     ) {
  //       return true;
  //     }
  //   }

  //   return false;
  // };
  // const areFieldsEmpty4 = (start, end) => {
  //   const flattenedItems = [];

  //   const extractItems = (items) => {
  //     for (const item of items) {
  //       if (item[3]) {
  //         flattenedItems.push({ num: parseFloat(item[0]), key: item[3] });
  //       }
  //     }
  //   };

  //   extractItems(part2);

  //   const filteredItems = flattenedItems.filter(
  //     (item) => item.num >= start && item.num <= end
  //   );

  //   for (const item of filteredItems) {
  //     const val = formData[item.key];

  //     if (val === undefined || val === null || val === "") {
  //       return true;
  //     }

  //     if (
  //       Array.isArray(val) &&
  //       val.some(
  //         (v) =>
  //           v === "" ||
  //           v === null ||
  //           v === undefined ||
  //           (Array.isArray(v) &&
  //             v.some((sub) => sub === "" || sub === null || sub === undefined))
  //       )
  //     ) {
  //       return true;
  //     }
  //   }

  //   return false;
  // };
const areFieldsEmpty = (start, end, items) => {
  if (!Array.isArray(items)) {
    console.warn("areFieldsEmpty: items is not iterable", items);
    return false; // treat as no fields to check
  }

  const flattenedItems = [];

  const extractItems = (items) => {
    for (const item of items) {
      // item[3] = field key
      // item[4] = isInputable (true/false)
      if (item[3] && item[4] !== false) {
        flattenedItems.push({ num: parseFloat(item[0]), key: item[3] });
      }
    }
  };

  extractItems(items);

  const filteredItems = flattenedItems.filter(
    (item) => item.num >= start && item.num <= end
  );

  for (const item of filteredItems) {
    const val = formData[item.key];

    if (val === undefined || val === null || val === "") {
      return true;
    }

    if (
      Array.isArray(val) &&
      val.some(
        (v) =>
          v === "" ||
          v === null ||
          v === undefined ||
          (Array.isArray(v) &&
            v.some((sub) => sub === "" || sub === null || sub === undefined))
      )
    ) {
      return true;
    }
  }

  return false;
};


  const validatePhone = (phone) => phoneRegex.test(phone);
  const validateEmail = (email) => emailRegex.test(email);

  const handleChange = (name, value, gaIndex = null, yearIndex = null) => {
    // Validate text fields
    if ([1, 2, 4].includes(currentStep)) {
      let applicableMaxLength = FIELD_MAX_LENGTH;
      if (name === "companyProfile") applicableMaxLength = PROFILE_MAX_LENGTH;
      else if (name === "comment") applicableMaxLength = COMMENT_MAX_LENGTH;

      if (typeof value === "string") {
        if (name === "companyProfile") {
          const wordCount = value.trim().split(/\s+/).length;
          if (wordCount >= 300) {
            alert("Maximum 300 words allowed.");
            return;
          }
        } else if (name === "comment") {
          const wordCount = value.trim().split(/\s+/).length;
          if (wordCount > 200) {
            alert("Maximum 200 words allowed in Comments.");
            return;
          }
        }
      }
    }

    if (["Organisationname", "authorityName", "contact_name"].includes(name)) {
      if (!/^[A-Za-z\s]*$/.test(value)) return;
      if (value.length > FIELD_MAX_LENGTH) {
        setFieldErrors((prev) => ({
          ...prev,
          [name]: `Input must not exceed ${FIELD_MAX_LENGTH} characters`,
        }));
        return;
      }
    }

    // Validate companyProfile and comment

    // Update form data
    setFormData((prev) => {
      if (gaIndex !== null && yearIndex !== null) {
        const updatedArray = [...prev[name]];
        updatedArray[gaIndex] = [...(updatedArray[gaIndex] || ["", ""])];
        updatedArray[gaIndex][yearIndex] = value;
        return { ...prev, [name]: updatedArray };
      } else if (gaIndex !== null) {
        const updatedArray = [...prev[name]];
        updatedArray[gaIndex] = value;
        return { ...prev, [name]: updatedArray };
      } else {
        if (name === "authorityPhone" || name === "contact_phone") {
          const numericValue = value
            .replace(/\D/g, "")
            .slice(0, PHONE_MAX_LENGTH);
          return { ...prev, [name]: numericValue };
        }
        return { ...prev, [name]: value };
      }
    });

    // Clear errors on valid input
    if (name === "Organisationname" && value.trim() && currentStep === 1) {
      setFieldErrors((prev) => ({ ...prev, Organisation_name: undefined }));
    }
    if (name === "mailingAddress" && value.trim() && currentStep === 1) {
      setFieldErrors((prev) => ({ ...prev, mailingAddress: undefined }));
    }
    if (name === "category" && value && currentStep === 1) {
      setFieldErrors((prev) => ({ ...prev, category: undefined }));
    }
    if (name === "authorityName" && value.trim() && currentStep === 2) {
      setFieldErrors((prev) => ({ ...prev, authorityName: undefined }));
    }
    if (name === "authorityTitle" && value.trim() && currentStep === 2) {
      setFieldErrors((prev) => ({ ...prev, authorityTitle: undefined }));
    }
    if (
      name === "authorityEmail" &&
      validateEmail(value) &&
      currentStep === 2
    ) {
      setFieldErrors((prev) => ({ ...prev, authorityEmail: undefined }));
    }
    if (
      name === "authorityPhone" &&
      validatePhone(value) &&
      currentStep === 2
    ) {
      setFieldErrors((prev) => ({ ...prev, authorityPhone: undefined }));
    }
    if (
      name === "contact_name" &&
      value.trim() &&
      !copyApplicantData &&
      currentStep === 2
    ) {
      setFieldErrors((prev) => ({ ...prev, contact_name: undefined }));
    }
    if (
      name === "contactEmail" &&
      (!value || validateEmail(value)) &&
      !copyApplicantData &&
      currentStep === 2
    ) {
      setFieldErrors((prev) => ({ ...prev, contact_email: undefined }));
    }
    if (
      name === "contact_phone" &&
      (!value || validatePhone(value)) &&
      !copyApplicantData &&
      currentStep === 2
    ) {
      setFieldErrors((prev) => ({ ...prev, contact_phone: undefined }));
    }
    if (name === "companyProfile" && value.trim() && currentStep === 2) {
      setFieldErrors((prev) => ({ ...prev, companyProfile: undefined }));
    }
  };

  const handleBlur = (name, value) => {
    const errors = {};

    if (name === "authorityEmail") {
      if (!value) {
        errors.authorityEmail = "Email is required";
      } else if (!validateEmail(value)) {
        errors.authorityEmail = "Invalid email format";
      }
    }
    if (name === "contact_name" && !value.trim() && !copyApplicantData) {
      errors.contact_name = "Contact name is required";
    }
    if (
      name === "contactEmail" &&
      value &&
      !validateEmail(value) &&
      !copyApplicantData
    ) {
      errors.contact_email = "Invalid email format";
    }
    if (
      name === "contact_phone" &&
      value &&
      !validatePhone(value) &&
      !copyApplicantData
    ) {
      errors.contact_phone = "Phone must be exactly 10 digits";
    }

    setFieldErrors((prev) => ({ ...prev, ...errors }));
  };

  const handleAttachmentChange = (key, field, value, event = null) => {
    if (field === "description" && value.length > FIELD_MAX_LENGTH) {
      setFieldErrors((prev) => ({
        ...prev,
        [`${key}.description`]: `Description must not exceed ${FIELD_MAX_LENGTH} characters`,
      }));
      return;
    }
    if (field === "file" && value) {
      const file = value;
      const maxSizeInBytes = 5 * 1024 * 1024;
      const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
      if (!allowedTypes.includes(file.type)) {
        setFieldErrors((prev) => ({
          ...prev,
          [key]: "Only JPG, PNG, and PDF files are allowed",
        }));
        if (event) event.target.value = null;
        return;
      }
      if (file.size > maxSizeInBytes) {
        setFieldErrors((prev) => ({
          ...prev,
          [key]: "File size must not exceed 5 MB",
        }));
        if (event) event.target.value = null;
        return;
      }
    }
    setFormData((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value },
    }));
    setFieldErrors((prev) => ({
      ...prev,
      [key]: undefined,
      [`${key}.description`]: undefined,
    }));
  };

  const handleApprovingAuthorityChange = (files) => {
    if (files && files.length > 0) {
      const file = files[0];
      const maxSizeInBytes = 5 * 1024 * 1024;
      const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
      if (!allowedTypes.includes(file.type)) {
        setFieldErrors((prev) => ({
          ...prev,
          approvingAuthoritySignature:
            "Only JPG, PNG, and PDF files are allowed",
        }));
        return;
      }
      if (file.size > maxSizeInBytes) {
        setFieldErrors((prev) => ({
          ...prev,
          approvingAuthoritySignature: "File size must not exceed 5 MB",
        }));
        return;
      }
      setFormData((prev) => ({ ...prev, approvingAuthoritySignature: file }));
      setFieldErrors((prev) => ({
        ...prev,
        approvingAuthoritySignature: undefined,
      }));
    }
  };

  const nextStep = () => {
    if (currentStep === 1) {
      // if (!formData.Organisationname) {
      //   alert("Organisation name is required.");
      //   return;
      // }
      if (!formData.mailingAddress.trim()) {
        alert("Mailing address is required.");
        return;
      }
    }
    if (currentStep === 2) {
      if (!formData.authorityName) {
        alert("Authority name is required.");
        return;
      }
      if (!formData.authorityTitle) {
        alert("Authority designation is required.");
        return;
      }
      if (!formData.authorityEmail || !validateEmail(formData.authorityEmail)) {
        alert("Please enter a valid Authority email.");
        return;
      }
      if (!formData.authorityLandline) {
        alert("Authority Landline is required");
        return;
      }

      if (!formData.contact_name) {
        alert("Contact name is required.");
        return;
      }
      if (formData.contact_email && !validateEmail(formData.contact_email)) {
        alert("Please enter a valid Contact email.");
        return;
      }
      if (
        formData.contact_phone &&
        !validatePhone(formData.contact_phone) &&
        !copyApplicantData
      ) {
        alert("Contact phone must be exactly 10 digits.");
        return;
      }
      if (!formData.companyProfile) {
        alert("Company Profile is required.");
        return;
      }
    }
    if (currentStep === 3 && areFieldsEmpty(1, 3.5)) {
      if (!window.confirm("Data not entered. Do you wish to continue?")) {
        return;
      }
    }
    if (currentStep === 4 && areFieldsEmpty(4, 7.5)) {
      if (!window.confirm("Data not entered. Do you wish to continue?")) {
        return;
      }
    }

    if (currentStep < 5) setCurrentStep((prev) => prev + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep((prev) => prev - 1);
  };

  // Save draft handler
  const handleSaveDraft = () => {
    localStorage.setItem("registrationCgdDraft", JSON.stringify({ formData }));
    handleSubmit(null);
    alert("Draft Saved!");
  };

  //Final Submit button
   const viewFile =  async (serverPath) => {
    // "https://award-backend.fipi.org.in/media/
    const extractPath = serverPath.split('/').filter(
  (val) =>
    val !== "" && // remove empty strings
    val !== "http:" &&
    val !== "https:" &&
    val !== "media" &&
    !val.startsWith("award") &&
    !val.startsWith("localhost")
).join('/');
    
    downloadFile(extractPath)
  }

  const finalSubmit = () => {
    handleSubmit(null, "submited");
  };

  // async function handleSubmit(event, clickType = "draft") {
  //   if (event) {
  //     event?.preventDefault();
  //   }
  //   console.log(
  //     "############################ Save the DAta on Server ##########################"
  //   );
  //   // 1) Make a local copy of formData so we don't rely on async state updates
  //   let payload = { ...formData };

  //   // 2) Merge user_info synchronously (don't call setFormData here)
  //   try {
  //     const raw = localStorage.getItem("user_info");
  //     if (raw) {
  //       const u = JSON.parse(raw);
  //       payload.userid = u.id ?? payload.userid;
  //       payload.firstname = u.first_name ?? payload.firstname;
  //       payload.lastname = u.last_name ?? payload.lastname;
  //       payload.companyName = u.organisation_name ?? payload.company_name;
  //       payload.Organisationname =
  //         u.Organisation_name ?? payload.Organisation_name;
  //     }
  //   } catch (err) {
  //     console.warn("Failed to load user_info:", err);
  //   }

  //   const fd = new FormData();

  //   // — Identity & contacts (field names match Django model exactly)
  //   fd.append("Organisation_name", formData.Organisationname || "");
  //   fd.append("category", formData.category || "CGD Company of the Year");
  //   fd.append("companyName", formData.companyName || "");
  //   fd.append("mailingAddress", formData.mailingAddress || "");

  //   // Authority & contact
  //   fd.append("authority_name", formData.authorityName || "");
  //   fd.append("authorityTitle", formData.authorityTitle || "");
  //   fd.append("authorityPhone", formData.authorityPhone || "");
  //   fd.append("authorityLandline", formData.authorityLandline || "");
  //   fd.append("authorityEmail", formData.authorityEmail || "");
  //   fd.append("form_id", formId);
  //   fd.append("form_mode", clickType);
  //   // Add Table Pk Id;
  //   if (PkId > 0) fd.append("id", PkId);

  //   if (payload.approvingAuthoritySignature instanceof File) {
  //     fd.append(
  //       "approvingAuthoritySignature",
  //       payload.approvingAuthoritySignature
  //     );
  //   }
  //   fd.append("contactName", formData.contact_name || "");
  //   fd.append("contactPhone", formData.contact_phone || "");
  //   fd.append("contactEmail", formData.contact_email || "");

  //   // Profile, comment, declaration
  //   fd.append("companyProfile", formData.companyProfile || "");
  //   fd.append("comment", formData.comment || "");
  //   fd.append("declaration", formData.declaration ? "true" : "false");

  //   // 3) GA-presence arrays (JSONField → stringify)
  //   fd.append("ga10YearMWP", JSON.stringify(formData.ga10YearMWP || []));
  //   fd.append("ga8YearMWP", JSON.stringify(formData.ga8YearMWP || []));
  //   fd.append("ga5YearMWP", JSON.stringify(formData.ga5YearMWP || []));
  //   fd.append("ga3YearMWP", JSON.stringify(formData.ga3YearMWP || []));
  //   fd.append("gaNoMWP", JSON.stringify(formData.gaNoMWP || []));

  //   // Single-value-per-GA arrays
  //   fd.append("gaNames", JSON.stringify(formData.gaNames || []));
  //   // fd.append("baseDate", JSON.stringify(formData.baseDate));
  //   fd.append("baseDate", STATIC_BASE_DATE);
  //   fd.append("startWorkDate", JSON.stringify(formData.startWorkDate || []));
  //   fd.append(
  //     "exclusivityPeriod",
  //     JSON.stringify(formData.exclusivityPeriod || [])
  //   );
  //   fd.append(
  //     "forceMajeureStart",
  //     JSON.stringify(formData.forceMajeureStart || [])
  //   );
  //   fd.append(
  //     "forceMajeureEnd",
  //     JSON.stringify(formData.forceMajeureEnd || [])
  //   );
  //   fd.append(
  //     "forceMajeureStart2",
  //     JSON.stringify(formData.forceMajeureStart2 || [])
  //   );
  //   fd.append(
  //     "forceMajeureEnd2",
  //     JSON.stringify(formData.forceMajeureEnd2 || [])
  //   );

  //   // Double-value-per-GA arrays
  //   fd.append("pngTotalMWP", JSON.stringify(formData.pngTotalMWP || []));
  //   fd.append("pngProRatedMWP", JSON.stringify(formData.pngProRatedMWP || []));
  //   fd.append("pngActual", JSON.stringify(formData.pngActual || []));
  //   fd.append("pngBilled", JSON.stringify(formData.pngBilled || []));
  //   fd.append("cngTotalMWP", JSON.stringify(formData.cngTotalMWP || []));
  //   fd.append("cngProRatedMWP", JSON.stringify(formData.cngProRatedMWP || []));
  //   fd.append("cngActual", JSON.stringify(formData.cngActual || []));
  //   fd.append(
  //     "pipelineTotalMWP",
  //     JSON.stringify(formData.pipelineTotalMWP || [])
  //   );
  //   fd.append(
  //     "pipelineProRatedMWP",
  //     JSON.stringify(formData.pipelineProRatedMWP || [])
  //   );
  //   fd.append("pipelineActual", JSON.stringify(formData.pipelineActual || []));
  //   fd.append("pngSalesVolume", JSON.stringify(formData.pngSalesVolume || []));
  //   fd.append("cngSalesVolume", JSON.stringify(formData.cngSalesVolume || []));
  //   fd.append("cbgIntake", JSON.stringify(formData.cbgIntake || []));
  //   fd.append("fatalities", JSON.stringify(formData.fatalities || []));
  //   fd.append(
  //     "totalHoursWorked",
  //     JSON.stringify(formData.totalHoursWorked || [])
  //   );
  //   fd.append(
  //     "lostTimeInjuries",
  //     JSON.stringify(formData.lostTimeInjuries || [])
  //   );
  //   fd.append(
  //     "totalRecordableIncident",
  //     JSON.stringify(formData.totalRecordableIncident || [])
  //   );
  //   fd.append("safetyAudits", JSON.stringify(formData.safetyAudits || []));

  //   // 4) Attachments (desc + file) ×4
  //   [1, 2, 3, 4].forEach((n) => {
  //     const slot = formData[`attachments${n}`] || {};
  //     fd.append(`attachments${n}_desc`, slot.description || "");
  //     if (slot.file instanceof File) {
  //       fd.append(`attachments${n}`, slot.file);
  //     }
  //   });

  //   // 5) Send to API
  //   try {
  //     const response = await apiClient.post("/registration-cgd/", fd, {
  //       headers: { "Content-Type": "multipart/form-data" },
  //     });

  //     // When User save the Data It will Prefill the form data and fill pkId
  //     prefillFormData();
  //   } catch (err) {
  //     console.error("registration-cgd submission error:", err);
  //     const serverMsg =
  //       err?.response?.data ?? err?.message ?? JSON.stringify(err);
  //     alert("Submission failed: " + JSON.stringify(serverMsg));
  //   }
  // }


  async function handleSubmit(event, clickType = "draft") {
  if (event) {
    event?.preventDefault();
  }
  console.log(
    "############################ Save the DAta on Server ##########################"
  );
  // 1) Make a local copy of formData so we don't rely on async state updates
  let payload = { ...formData };

  // 2) Merge user_info synchronously (don't call setFormData here)
  try {
    const raw = localStorage.getItem("user_info");
    if (raw) {
      const u = JSON.parse(raw);
      payload.userid = u.id ?? payload.userid;
      payload.firstname = u.first_name ?? payload.firstname;
      payload.lastname = u.last_name ?? payload.lastname;
      payload.companyName = u.organisation_name ?? payload.company_name;
      payload.Organisationname =
        u.Organisation_name ?? payload.Organisation_name;
    }
  } catch (err) {
    console.warn("Failed to load user_info:", err);
  }

  const fd = new FormData();

  // — Identity & contacts (field names match Django model exactly)
  fd.append("Organisation_name", formData.Organisationname || "");
  fd.append("category", formData.category || "CGD Company of the Year");
  fd.append("companyName", formData.companyName || "");
  fd.append("mailingAddress", formData.mailingAddress || "");

  // Authority & contact
  fd.append("authority_name", formData.authorityName || "");
  fd.append("authorityTitle", formData.authorityTitle || "");
  fd.append("authorityPhone", formData.authorityPhone || "");
  fd.append("authorityLandline", formData.authorityLandline || "");
  fd.append("authorityEmail", formData.authorityEmail || "");
  fd.append("form_id", formId);
  fd.append("form_mode", clickType);
  // Add Table Pk Id;
  if (PkId > 0) fd.append("id", PkId);

  if (payload.approvingAuthoritySignature instanceof File) {
    fd.append(
      "approvingAuthoritySignature",
      payload.approvingAuthoritySignature
    );
  }
  fd.append("contactName", formData.contact_name || "");
  fd.append("contactPhone", formData.contact_phone || "");
  fd.append("contactEmail", formData.contact_email || "");

  // Profile, comment, declaration
  fd.append("companyProfile", formData.companyProfile || "");
  fd.append("comment", formData.comment || "");
  fd.append("declaration", formData.declaration ? "true" : "false");

  // 3) GA-presence arrays (JSONField → stringify)
  fd.append("ga10YearMWP", JSON.stringify(formData.ga10YearMWP || []));
  fd.append("ga8YearMWP", JSON.stringify(formData.ga8YearMWP || []));
  fd.append("ga5YearMWP", JSON.stringify(formData.ga5YearMWP || []));
  fd.append("ga3YearMWP", JSON.stringify(formData.ga3YearMWP || []));
  fd.append("gaNoMWP", JSON.stringify(formData.gaNoMWP || []));

  // Single-value-per-GA arrays
  fd.append("gaNames", JSON.stringify(formData.gaNames || []));

  // ---------- NEW: send baseDate for every GA ----------
  const STATIC_BASE_DATE = "2025-03-31";
  // Use the number of GA names as the count (matches "for every GA i add")
  const gaCount = (Array.isArray(formData.gaNames) && formData.gaNames.length) || 0;
  // If there are GAs, create an array filled with STATIC_BASE_DATE repeated gaCount times.
  // If no GAs, send an empty array (backend-side JSONField will receive []).
  const baseDatePayload = gaCount > 0 ? Array(gaCount).fill(STATIC_BASE_DATE) : [];
  fd.append("baseDate", JSON.stringify(baseDatePayload));
  // ----------------------------------------------------

  fd.append("startWorkDate", JSON.stringify(formData.startWorkDate || []));
  fd.append(
    "exclusivityPeriod",
    JSON.stringify(formData.exclusivityPeriod || [])
  );
  fd.append(
    "forceMajeureStart",
    JSON.stringify(formData.forceMajeureStart || [])
  );
  fd.append(
    "forceMajeureEnd",
    JSON.stringify(formData.forceMajeureEnd || [])
  );
  fd.append(
    "forceMajeureStart2",
    JSON.stringify(formData.forceMajeureStart2 || [])
  );
  fd.append(
    "forceMajeureEnd2",
    JSON.stringify(formData.forceMajeureEnd2 || [])
  );

  // Double-value-per-GA arrays
  fd.append("pngTotalMWP", JSON.stringify(formData.pngTotalMWP || []));
  fd.append("pngProRatedMWP", JSON.stringify(formData.pngProRatedMWP || []));
  fd.append("pngActual", JSON.stringify(formData.pngActual || []));
  fd.append("pngBilled", JSON.stringify(formData.pngBilled || []));
  fd.append("cngTotalMWP", JSON.stringify(formData.cngTotalMWP || []));
  fd.append("cngProRatedMWP", JSON.stringify(formData.cngProRatedMWP || []));
  fd.append("cngActual", JSON.stringify(formData.cngActual || []));
  fd.append(
    "pipelineTotalMWP",
    JSON.stringify(formData.pipelineTotalMWP || [])
  );
  fd.append(
    "pipelineProRatedMWP",
    JSON.stringify(formData.pipelineProRatedMWP || [])
  );
  fd.append("pipelineActual", JSON.stringify(formData.pipelineActual || []));
  fd.append("pngSalesVolume", JSON.stringify(formData.pngSalesVolume || []));
  fd.append("cngSalesVolume", JSON.stringify(formData.cngSalesVolume || []));
  fd.append("cbgIntake", JSON.stringify(formData.cbgIntake || []));
  fd.append("fatalities", JSON.stringify(formData.fatalities || []));
  fd.append(
    "totalHoursWorked",
    JSON.stringify(formData.totalHoursWorked || [])
  );
  fd.append(
    "lostTimeInjuries",
    JSON.stringify(formData.lostTimeInjuries || [])
  );
  fd.append(
    "totalRecordableIncident",
    JSON.stringify(formData.totalRecordableIncident || [])
  );
  fd.append("safetyAudits", JSON.stringify(formData.safetyAudits || []));

  // 4) Attachments (desc + file) ×4
  [1, 2, 3, 4].forEach((n) => {
    const slot = formData[`attachments${n}`] || {};
    fd.append(`attachments${n}_desc`, slot.description || "");
    if (slot.file instanceof File) {
      fd.append(`attachments${n}`, slot.file);
    }
  });

  // 5) Send to API
  try {
    const response = await apiClient.post("/registration-cgd/", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    // When User save the Data It will Prefill the form data and fill pkId
    prefillFormData();
  } catch (err) {
    console.error("registration-cgd submission error:", err);
    const serverMsg =
      err?.response?.data ?? err?.message ?? JSON.stringify(err);
    alert("Submission failed: " + JSON.stringify(serverMsg));
  }
}

  const handlePrint = () => {
  const getYearValue = (key, gaIndex = 0, yearIndex = 0) => {
    try {
      const all = formData?.[key];
      if (!all) return "";
      const item = all[gaIndex];
      if (item == null) return "";

      if (Array.isArray(item)) return item[yearIndex] ?? "";
      if (typeof item === "object") {
        return (
          item[yearIndex] ??
          item[String(yearIndex)] ??
          item["0"] ??
          item["1"] ??
          item.value ??
          item.v ??
          item["2024-25"] ??
          item["2023-24"] ??
          ""
        );
      }
      // primitive (string/number) -> return entire value (not character)
      return String(item);
    } catch (e) {
      return "";
    }
  };

  const escapeHtml = (s) =>
    String(s ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

    const printContent = `
    <div style="font-family: Arial, sans-serif; padding: 20px;">
      <h1 style="text-align: center; color: #1e40af;">${awardTitle}</h1>
      <h2>Organization & Contact Details:</h2>
      <p><strong>Organisation Name:</strong> ${
        formData.Organisationname || ""
      }</p>
      <p><strong>Postal Address:</strong> ${formData.mailingAddress || ""}</p>
      <h2>Approving Authority Details</h2>
      <p><strong>Approving Authority Name:</strong>  ${
        formData.authorityName || ""
      }</p>
      <p><strong>Approving Authority Designation:</strong> ${
        formData.authorityTitle || ""
      }</p>
      <p><strong>Approving Authority Phone:</strong> ${
        formData.authorityPhone || ""
      }</p>
          <p><strong>Approving Authority Landline:</strong> ${
            formData.authorityLandline || ""
          }</p>
      <p><strong>Approving Authority Email:</strong> ${
        formData.authorityEmail || ""
      }</p>
      <h2>Nodal Official Contact Details:</h2>
      <p><strong>Contact Name:</strong> ${formData.contact_name || ""}</p>
      <p><strong>Contact Phone:</strong> ${formData.contact_phone || ""}</p>
      <p><strong>Contact Email:</strong> ${formData.contact_email || ""}</p>
      <p><strong>Brief write up on company’s profile:</strong> ${
        formData.companyProfile || ""
      }
      
        <h2>Quantitative Information - Part 1</h2>
        <h3>General Information</h3>
        <table style="width: 100%; border-collapse: collapse; margin-top: 16px;">
          <thead>
            <tr>
              <th style="border: 1px solid #000; padding: 8px;">S.No</th>
              <th style="border: 1px solid #000; padding: 8px;">Particulars</th>
              <th style="border: 1px solid #000; padding: 8px;">Unit</th>
              <th style="border: 1px solid #000; padding: 8px;">2024-25</th>
             
            </tr>
          </thead>
          <tbody>
            ${part1
              .slice(0, 5)
              .map(
                ([num, label, unit, key]) => `
              <tr>
                <td style="border: 1px solid #000; padding: 8px;">${num}</td>
                <td style="border: 1px solid #000; padding: 8px;">${label}</td>
                <td style="border: 1px solid #000; padding: 8px;">${unit}</td>
                <td style="border: 1px solid #000; padding: 8px;">${
                  formData[key]?.[0] || ""
                }</td>
               
              </tr>
            `
              )
              .join("")}
          </tbody>
        </table>
        <h3>GA-Specific Information</h3>
        <table style="width: 100%; border-collapse: collapse; margin-top: 16px;">
          <thead>
            <tr>
              <th style="border: 1px solid #000; padding: 8px;">S.No</th>
              <th style="border: 1px solid #000; padding: 8px;">Particulars</th>
              <th style="border: 1px solid #000; padding: 8px;">Unit</th>
              ${Array.from(
                { length: numGAs },
                (_, i) => `
                <th style="border: 1px solid #000; padding: 8px;">GA${
                  i + 1
                }</th>
              `
              ).join("")}
            </tr>
          </thead>
          <tbody>
           ${part1
  .slice(5, 6)
  .map(
    ([num, label, unit, key, isInput]) => `
    <tr>
      <td style="border: 1px solid #000; padding: 8px;">${num}</td>
      <td style="border: 1px solid #000; padding: 8px;">${label}</td>
      <td style="border: 1px solid #000; padding: 8px;">${unit}</td>
      ${
        isInput
          ? Array.from({ length: numGAs }, (_, gaIndex) => `
              <td style="border: 1px solid #000; padding: 8px;">${escapeHtml(getYearValue(key, gaIndex, 0))} 31/03/2025</td>
            `).join("")
          : `<td style="border: 1px solid #000; padding: 8px;" colspan="${numGAs}">31/03/2025</td>`
      }
    </tr>
  `
  )
  .join("")}

            ${part1
              .slice(6, 12)
              .map(
                ([num, label, unit, key, isInput]) => `
              <tr>
                <td style="border: 1px solid #000; padding: 8px;">${num}</td>
                <td style="border: 1px solid #000; padding: 8px;">${label}</td>
                <td style="border: 1px solid #000; padding: 8px;">${unit}</td>
                ${
                  isInput
                    ? Array.from(
                        { length: numGAs },
                        (_, gaIndex) => `
                  <td style="border: 1px solid #000; padding: 8px;">${
                    formData[key]?.[gaIndex] || ""
                  }</td>
                `
                      ).join("")
                    : `
                  <td style="border: 1px solid #000; padding: 8px;" colspan="${numGAs}">
                    ${calculatedRows.find((row) => row.key === key)?.value || 0}
                  </td>
                `
                }
              </tr>
            `
              )
              .join("")}
          </tbody>
        </table>
        <h3>D-PNG Connection</h3>
        <table style="width: 100%; border-collapse: collapse; margin-top: 16px;">
          <thead>
            <tr>
              <th style="border: 1px solid #000; padding: 8px;">S.No</th>
              <th style="border: 1px solid #000; padding: 8px;">Particulars</th>
              <th style="border: 1px solid #000; padding: 8px;">Unit</th>
              ${Array.from(
                { length: numGAs },
                (_, i) => `
                <th style="border: 1px solid #000; padding: 8px;">GA${
                  i + 1
                } (2024-25) </th>
                <th style="border: 1px solid #000; padding: 8px;">GA${
                  i + 1
                } (2023-24) </th>
              `
              ).join("")}
            </tr>
          </thead>
          <tbody>
          ${part1
            .slice(13, 14) // single row, first-index-only behaviour
            .map(
              ([num, label, unit, key, isInput]) => `
              <tr>
                <td style="border: 1px solid #000; padding: 8px;">${num}</td>
                <td style="border: 1px solid #000; padding: 8px;">${label}</td>
                <td style="border: 1px solid #000; padding: 8px;">${unit}</td>
                ${
                  isInput
                    ? Array.from({ length: numGAs }, (_, gaIndex) => {
                        const firstVal = getYearValue(key, gaIndex, 0) || "";
                        const secondVal = ""; // intentionally blank
                        return `
                          <td colspan ="2" style="border: 1px solid #000; padding: 8px;">${firstVal}</td>
                          
                        `;
                      }).join("")
                    : `<td style="border: 1px solid #000; padding: 8px;" colspan="${numGAs * 2}">${calculatedRows.find((row) => row.key === key)?.value || 0}</td>`
                }
              </tr>
            `
            )
            .join("")}
            ${part1
              .slice(14)
              .map(
                ([num, label, unit, key, isInput]) => `
              <tr>
                <td style="border: 1px solid #000; padding: 8px;">${num}</td>
                <td style="border: 1px solid #000; padding: 8px;">${label}</td>
                <td style="border: 1px solid #000; padding: 8px;">${unit}</td>
                ${
                  isInput
                    ? Array.from(
                        { length: numGAs },
                        (_, gaIndex) => `
                  <td style="border: 1px solid #000; padding: 8px;">${
                    formData[key]?.[gaIndex]?.[0] || ""
                  }</td>
                  <td style="border: 1px solid #000; padding: 8px;">${
                    formData[key]?.[gaIndex]?.[1] || ""
                  }</td>
                `
                      ).join("")
                    : `
                  <td style="border: 1px solid #000; padding: 8px;" colspan="${
                    numGAs * 2
                  }">
                    ${calculatedRows.find((row) => row.key === key)?.value || 0}
                  </td>
                `
                }
              </tr>
            `
              )
              .join("")}
          </tbody>
        </table>
        <h2>Quantitative Information - Part 2</h2>
        <h3>CNG Stations</h3>
        <table style="width: 100%; border-collapse: collapse; margin-top: 16px;">
          <thead>
            <tr>
              <th style="border: 1px solid #000; padding: 8px;">S. No.</th>
              <th style="border: 1px solid #000; padding: 8px;">Particulars</th>
              <th style="border: 1px solid #000; padding: 8px;">Unit</th>
              ${Array.from(
                { length: numGAs },
                (_, i) => `
                <th style="border: 1px solid #000; padding: 8px;">GA${
                  i + 1
                } (2024-25)</th>
                <th style="border: 1px solid #000; padding: 8px;">GA${
                  i + 1
                } (2023-24)</th>
              `
              ).join("")}
            </tr>
          </thead>
          <tbody>
         ${part2
  .slice(0, 1)
  .map(
    ([num, label, unit, key, isInput]) => `
    <tr>
      <td style="border: 1px solid #000; padding: 8px;">${num}</td>
      <td style="border: 1px solid #000; padding: 8px;">${label}</td>
      <td style="border: 1px solid #000; padding: 8px;">${unit}</td>
      ${
        isInput
          ? Array.from({ length: numGAs }, (_, gaIndex) => {
              const v0 = escapeHtml(getYearValue(key, gaIndex, 0));
              const v1 = escapeHtml(getYearValue(key, gaIndex, 1));
              return `
                <td colspan ="2" style="border: 1px solid #000; padding: 8px;">${v0}</td>
                
              `;
            }).join("")
          : `
            <td style="border: 1px solid #000; padding: 8px;" colspan="${numGAs * 2}">
              ${calculatedRows.find((row) => row.key === key)?.value || 0}
            </td>
          `
      }
    </tr>
  `
  )
  .join("")}

            ${part2
              .slice(1, 3)
              .map(
                ([num, label, unit, key, isInput]) => `
              <tr>
                <td style="border: 1px solid #000; padding: 8px;">${num}</td>
                <td style="border: 1px solid #000; padding: 8px;">${label}</td>
                <td style="border: 1px solid #000; padding: 8px;">${unit}</td>
                ${
                  isInput
                    ? Array.from(
                        { length: numGAs },
                        (_, gaIndex) => `
                  <td style="border: 1px solid #000; padding: 8px;">${
                    formData[key]?.[gaIndex]?.[0] || ""
                  }</td>
                  <td style="border: 1px solid #000; padding: 8px;">${
                    formData[key]?.[gaIndex]?.[1] || ""
                  }</td>
                `
                      ).join("")
                    : `
                  <td style="border: 1px solid #000; padding: 8px;" colspan="${
                    numGAs * 2
                  }">
                    ${calculatedRows.find((row) => row.key === key)?.value || 0}
                  </td>
                `
                }
              </tr>
            `
              )
              .join("")}
          </tbody>
        </table>
        <h3>Inch-km of Pipeline Laid</h3>
        <table style="width: 100%; border-collapse: collapse; margin-top: 16px;">
          <thead>
            <tr>
              <th style="border: 1px solid #000; padding: 8px;">S. No.</th>
              <th style="border: 1px solid #000; padding: 8px;">Particulars</th>
              <th style="border: 1px solid #000; padding: 8px;">Unit</th>
              ${Array.from(
                { length: numGAs },
                (_, i) => `
                <th style="border: 1px solid #000; padding: 8px;">GA${
                  i + 1
                } (2024-25)</th>
                <th style="border: 1px solid #000; padding: 8px;">GA${
                  i + 1
                } (2023-24)</th>
              `
              ).join("")}
            </tr>
          </thead>
          <tbody>
           ${part2
  .slice(3,4)
  .map(
    ([num, label, unit, key, isInput]) => `
    <tr>
      <td style="border: 1px solid #000; padding: 8px;">${num}</td>
      <td style="border: 1px solid #000; padding: 8px;">${label}</td>
      <td style="border: 1px solid #000; padding: 8px;">${unit}</td>
      ${
        isInput
          ? Array.from({ length: numGAs }, (_, gaIndex) => {
              const v0 = escapeHtml(getYearValue(key, gaIndex, 0));
              const v1 = escapeHtml(getYearValue(key, gaIndex, 1));
              return `
                <td colspan ="2" style="border: 1px solid #000; padding: 8px;">${v0}</td>
               
              `;
            }).join("")
          : `
            <td style="border: 1px solid #000; padding: 8px;" colspan="${numGAs * 2}">
              ${calculatedRows.find((row) => row.key === key)?.value || 0}
            </td>
          `
      }
    </tr>
  `
  )
  .join("")}

          ${part2
              .slice(4, 6)
              .map(
                ([num, label, unit, key, isInput]) => `
              <tr>
                <td style="border: 1px solid #000; padding: 8px;">${num}</td>
                <td style="border: 1px solid #000; padding: 8px;">${label}</td>
                <td style="border: 1px solid #000; padding: 8px;">${unit}</td>
                ${
                  isInput
                    ? Array.from(
                        { length: numGAs },
                        (_, gaIndex) => `
                  <td style="border: 1px solid #000; padding: 8px;">${
                    formData[key]?.[gaIndex]?.[0] || ""
                  }</td>
                  <td style="border: 1px solid #000; padding: 8px;">${
                    formData[key]?.[gaIndex]?.[1] || ""
                  }</td>
                `
                      ).join("")
                    : `
                  <td style="border: 1px solid #000; padding: 8px;" colspan="${
                    numGAs * 2
                  }">
                    ${calculatedRows.find((row) => row.key === key)?.value || 0}
                  </td>
                `
                }
              </tr>
            `
              )
              .join("")}
          </tbody>
        </table>
        <h3>Natural Gas Sales & CBG Intake</h3>
        <table style="width: 100%; border-collapse: collapse; margin-top: 16px;">
          <thead>
            <tr>
              <th style="border: 1px solid #000; padding: 8px;">S. No.</th>
              <th style="border: 1px solid #000; padding: 8px;">Particulars</th>
              <th style="border: 1px solid #000; padding: 8px;">Unit</th>
              ${Array.from(
                { length: numGAs },
                (_, i) => `
                <th style="border: 1px solid #000; padding: 8px;">GA${
                  i + 1
                } (2024-25)</th>
                <th style="border: 1px solid #000; padding: 8px;">GA${
                  i + 1
                } (2023-24)</th>
              `
              ).join("")}
            </tr>
          </thead>
          <tbody>
            ${part2
              .slice(6, 9)
              .map(
                ([num, label, unit, key]) => `
              <tr>
                <td style="border: 1px solid #000; padding: 8px;">${num}</td>
                <td style="border: 1px solid #000; padding: 8px;">${label}</td>
                <td style="border: 1px solid #000; padding: 8px;">${unit}</td>
                ${Array.from(
                  { length: numGAs },
                  (_, gaIndex) => `
                  <td style="border: 1px solid #000; padding: 8px;">${
                    formData[key]?.[gaIndex]?.[0] || ""
                  }</td>
                  <td style="border: 1px solid #000; padding: 8px;">${
                    formData[key]?.[gaIndex]?.[1] || ""
                  }</td>
                `
                ).join("")}
              </tr>
            `
              )
              .join("")}
          </tbody>
        </table>
        <h3>Safety</h3>
        <table style="width: 100%; border-collapse: collapse; margin-top: 16px;">
          <thead>
            <tr>
              <th style="border: 1px solid #000; padding: 8px;">S. No.</th>
              <th style="border: 1px solid #000; padding: 8px;">Particulars</th>
              <th style="border: 1px solid #000; padding: 8px;">Unit</th>
              ${Array.from(
                { length: numGAs },
                (_, i) => `
                <th style="border: 1px solid #000; padding: 8px;">GA${
                  i + 1
                } (2024-25)</th>
                <th style="border: 1px solid #000; padding: 8px;">GA${
                  i + 1
                } (2023-24)</th>
              `
              ).join("")}
            </tr>
          </thead>
          <tbody>
            ${part2
              .slice(9)
              .map(
                ([num, label, unit, key]) => `
              <tr>
                <td style="border: 1px solid #000; padding: 8px;">${num}</td>
                <td style="border: 1px solid #000; padding: 8px;">${label}</td>
                <td style="border: 1px solid #000; padding: 8px;">${unit}</td>
                ${Array.from(
                  { length: numGAs },
                  (_, gaIndex) => `
                  <td style="border: 1px solid #000; padding: 8px;">${
                    formData[key]?.[gaIndex]?.[0] || ""
                  }</td>
                  <td style="border: 1px solid #000; padding: 8px;">${
                    formData[key]?.[gaIndex]?.[1] || ""
                  }</td>
                `
                ).join("")}
              </tr>
            `
              )
              .join("")}
          </tbody>
        </table>
         <h2>Attachments</h2>
      <table style="width: 100%; border-collapse: collapse; margin-top: 16px;">
        <thead>
          <tr>
            <th style="border: 1px solid #000; padding: 8px;">S.No</th>
            <th style="border: 1px solid #000; padding: 8px;">Description</th>
            <th style="border: 1px solid #000; padding: 8px;">File Name</th>
          </tr>
        </thead>
        <tbody>
          ${[1, 2, 3, 4]
            .map(
              (i) => `
            <tr>
              <td style="border: 1px solid #000; padding: 8px; width:10px">${i}</td>
              <td style="border: 1px solid #000; padding: 8px;">${
                formData[`attachments${i}`].description || ""
              }</td>
              <td style="border: 1px solid #000; padding: 8px;">${
                formData[`attachments${i}`].file?.name || ""
              }</td>
            </tr>
          `
            )
            .join("")}
        </tbody>
      </table>
       <h2>Comments</h2>
      <p>${formData.comment || ""}</p>
      <h2>Declaration</h2>
      <p>I declare that the information submitted is true and complete.</p> 
      <br/>
      <p><strong>Approving Authority Name:</strong> ${
        formData.authorityName || ""
      }</p>
      <p><strong>Approving Authority Designation:</strong> ${
        formData.authorityTitle || ""
      }</p>
      <p><strong>Signed on:</strong> ${
        formData.signatureDate || new Date().toLocaleDateString()
      }</p>
      <p><strong>Approving Authority Signature:</strong></p>
    </div>
  `;

    const printWindow = window.open("", "_blank", "height=600,width=800");

    printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8">
        <title>${awardTitle || "Form Print"}</title>
      </head>
      <body>
        ${printContent}
      </body>
    </html>
  `);

    printWindow.document.close();
    printWindow.print();
  };
  const renderStepContent = () => {
    const progress = ((currentStep - 1) / 4) * 100;
    return (
      <div
        className="form-step"
        role="form"
        aria-label={`Step ${currentStep} of 5`}
      >
        <div className="progress-bar" aria-hidden="true">
          <div className="progress" style={{ width: `${progress}%` }}></div>
        </div>
        {currentStep === 1 && (
          <div>
            <h3 className="step-title">Step 1: Organization Details</h3>
            <div className="form-group">
              <label htmlFor="Organisationname">
                Organisation Name:{" "}
                {/* <span className="text-red" aria-hidden="true">
                  *
                </span> */}
              </label>
              <input
                id="Organisationname"
                type="text"
                name="Organisationname"
                value={formData.Organisationname}
                // onChange={(e) =>handleChange("Organisationname", e.target.value)}
                disabled={true}
                // onBlur={(e) => handleBlur("Organisationname", e.target.value)}
                className={`form-input ${
                  fieldErrors.Organisationname ? "has-error" : ""
                }`}
                maxLength={FIELD_MAX_LENGTH}
              />
              {/* {fieldErrors.Organisationname && (
                <span
                  className="error-tooltip"
                  id="Organisationname-error"
                  role="alert"
                >
                  {fieldErrors.Organisationname}
                </span>
              )} */}
            </div>
            <div className="form-group">
              <label htmlFor="mailingAddress">
                Postal Address:{" "}
                <span className="text-red" aria-hidden="true">
                  *
                </span>
              </label>
              <textarea
                id="mailingAddress"
                name="mailingAddress"
                value={formData.mailingAddress}
                onChange={(e) => handleChange("mailingAddress", e.target.value)}
                onBlur={(e) => handleBlur("mailingAddress", e.target.value)}
                className={`form-textarea ${
                  fieldErrors.mailingAddress ? "has-error" : ""
                }`}
                rows={3}
                maxLength={FIELD_MAX_LENGTH}
                placeholder="Enter postal address"
                aria-describedby="mailingAddress-error"
                required
              />
            </div>
          </div>
        )}
        {currentStep === 2 && (
          <div>
            <h3 className="step-title">
              Step 2: Approving Authority & Contact Details
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div
                className="step-section"
                aria-labelledby="approving-authority-heading"
              >
                <h4 id="approving-authority-heading">Approving Authority</h4>
                <p className="note">
                  Approving authority should be concerned Director /Board level
                  executive.{" "}
                </p>
                <div className="form-group">
                  <label htmlFor="authorityName">
                    Name:{" "}
                    <span className="text-red" aria-hidden="true">
                      *
                    </span>
                  </label>
                  <input
                    id="authorityName"
                    type="text"
                    name="authorityName"
                    value={formData.authorityName}
                    onChange={(e) =>
                      handleChange("authorityName", e.target.value)
                    }
                    onBlur={(e) => handleBlur("authorityName", e.target.value)}
                    className={`form-input ${
                      fieldErrors.authorityName ? "has-error" : ""
                    }`}
                    placeholder="Name"
                    maxLength={FIELD_MAX_LENGTH}
                    aria-describedby="authorityName-error"
                    required
                  />
                  {fieldErrors.authorityName && (
                    <span
                      className="error-tooltip"
                      id="authorityName-error"
                      role="alert"
                    >
                      {fieldErrors.authorityName}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="authorityTitle">
                    Designation:{" "}
                    <span className="text-red" aria-hidden="true">
                      *
                    </span>
                  </label>
                  <input
                    id="authorityTitle"
                    type="text"
                    name="authorityTitle"
                    value={formData.authorityTitle}
                    onChange={(e) =>
                      handleChange("authorityTitle", e.target.value)
                    }
                    onBlur={(e) => handleBlur("authorityTitle", e.target.value)}
                    className={`form-input ${
                      fieldErrors.authorityTitle ? "has-error" : ""
                    }`}
                    placeholder="Designation"
                    maxLength={FIELD_MAX_LENGTH}
                    aria-describedby="authorityTitle-error"
                    required
                  />
                  {fieldErrors.authorityTitle && (
                    <span
                      className="error-tooltip"
                      id="authorityTitle-error"
                      role="alert"
                    >
                      {fieldErrors.authorityTitle}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="authorityPhone">
                    Landline:{" "}
                    <span className="text-red" aria-hidden="true">
                      *
                    </span>
                  </label>
                  <input
                    id="authorityLandline"
                    type="number"
                    name="authorityLandline"
                    value={formData.authorityLandline}
                    // onChange={handleChange}
                    onChange={(e) =>
                      handleChange("authorityLandline", e.target.value)
                    }
                    className={`form-input ${
                      !formData.authorityLandline && currentStep === 2
                        ? "has-error"
                        : ""
                    }`}
                    placeholder="Landline number"
                    aria-required="true"
                    aria-describedby={
                      fieldErrors.authorityLandline
                        ? "authorityLandline-error"
                        : undefined
                    }
                  />
                  {!formData.authorityLandline && currentStep === 2 && (
                    <span className="error-tooltip">
                      Authority Landline Number is required
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="authorityPhone">
                    Mobile:{" "}
                    {/* <span className="text-red" aria-hidden="true">
                      *
                    </span> */}
                  </label>
                  <input
                    id="authorityPhone"
                    type="tel"
                    name="authorityPhone"
                    value={formData.authorityPhone}
                    // onChange={handleChange}
                    onChange={(e) =>
                      handleChange("authorityPhone", e.target.value)
                    }
                    className="form-input"
                    placeholder="Phone number"
                    maxLength={PHONE_MAX_LENGTH}
                    aria-required="true"
                    // aria-describedby={fieldErrors.authorityPhone ? 'authorityPhone-error' : undefined}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="authorityEmail">
                    Email:{" "}
                    <span className="text-red" aria-hidden="true">
                      *
                    </span>
                  </label>
                  <input
                    id="authorityEmail"
                    type="email"
                    name="authorityEmail"
                    value={formData.authorityEmail}
                    onChange={(e) =>
                      handleChange("authorityEmail", e.target.value)
                    }
                    onBlur={(e) => handleBlur("authorityEmail", e.target.value)}
                    className={`form-input ${
                      fieldErrors.authorityEmail ? "has-error" : ""
                    }`}
                    placeholder="E-mail address"
                    maxLength={FIELD_MAX_LENGTH}
                    aria-describedby="authorityEmail-error"
                    required
                  />
                  {fieldErrors.authorityEmail && (
                    <span
                      className="error-tooltip"
                      id="authorityEmail-error"
                      role="alert"
                    >
                      {fieldErrors.authorityEmail}
                    </span>
                  )}
                </div>
              </div>
              <div className="step-section" aria-labelledby="contacts-heading">
                <h4 id="contacts-heading">
                  Contacts (Nodal Officials){" "}
                  <span className="text-red" aria-hidden="true">
                    *
                  </span>
                </h4>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      name="copyApplicantData"
                      // checked={copyApplicantData}
                      onChange={handleCopyApplicantToggle}
                      className="form-checkbox"
                    />{" "}
                    Same as applicant
                  </label>
                </div>

                <div className="form-group">
                  <label htmlFor="contact_name">
                    Name{" "}
                    <span aria-hidden="true" className="text-red">
                      *
                    </span>
                  </label>
                  <input
                    id="contact_name"
                    name="contact_name"
                    type="text"
                    maxLength={FIELD_MAX_LENGTH}
                    value={formData.contact_name || ""}
                    onChange={(e) =>
                      handleChange("contact_name", e.target.value)
                    }
                    onBlur={(e) => handleBlur("contact_name", e.target.value)}
                    placeholder="Contact name"
                    disabled={copyApplicantData}
                    className={`form-input ${
                      !formData.contact_name && currentStep === 2
                        ? "has-error"
                        : ""
                    }`}
                    aria-describedby="contact_name-error"
                    required
                  />
                  {fieldErrors.contact_name && (
                    <p id="contact_name-error" className="text-red">
                      {fieldErrors.contact_name}
                    </p>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="contact_phone">
                    Phone:
                    <span aria-hidden="true" className="text-red">
                      *
                    </span>
                  </label>
                  <input
                    id="contact_phone"
                    name="contact_phone"
                    type="tel"
                    maxLength={PHONE_MAX_LENGTH}
                    value={formData.contact_phone || ""}
                    onChange={(e) =>
                      handleChange("contact_phone", e.target.value)
                    }
                    onBlur={(e) => handleBlur("contact_phone", e.target.value)}
                    aria-describedby="contact_phone-error"
                    disabled={copyApplicantData}
                    placeholder="10-digit phone number"
                    className={`form-input ${
                      !formData.contact_phone && currentStep === 2
                        ? "has-error"
                        : ""
                    }`}
                    required
                  />
                  {fieldErrors.contact_phone && (
                    <p id="contact_phone-error" className="text-red">
                      {fieldErrors.contact_phone}
                    </p>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="contact_email">Email</label>
                  <input
                    id="contact_email"
                    name="contact_email"
                    type="email"
                    maxLength={FIELD_MAX_LENGTH}
                    value={formData.contact_email || ""}
                    onChange={(e) =>
                      handleChange("contact_email", e.target.value)
                    }
                    onBlur={(e) => handleBlur("contact_email", e.target.value)}
                    placeholder="Contact email"
                    disabled={copyApplicantData}
                    className={`form-input ${
                      fieldErrors.contact_email ? "has-error" : ""
                    }`}
                    aria-describedby="contact_email-error"
                  />
                  {fieldErrors.contact_email && (
                    <p id="contact_email-error" className="text-red">
                      {fieldErrors.contact_email}
                    </p>
                  )}
                </div>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="companyProfile">
                Brief write up on company’s profile{" "}
                <span className="text-red" aria-hidden="true">
                  *
                </span>
              </label>
              <p className="note">(within 300 words)</p>
              <textarea
                id="companyProfile"
                name="companyProfile"
                value={formData.companyProfile}
                onChange={(e) => handleChange("companyProfile", e.target.value)}
                onBlur={(e) => handleBlur("companyProfile", e.target.value)}
                className={`form-textarea ${
                  fieldErrors.companyProfile ? "has-error" : ""
                }`}
                rows={6}
                // maxLength={PROFILE_MAX_LENGTH}
                aria-describedby="companyProfile-error"
                required
              />
             
            </div>
          </div>
        )}
        {currentStep === 3 && (
          <div>
            <h3 className="step-title">
              Step 3: Quantitative Information Part 1
            </h3>

            {/* General Info */}
            <h4>General Information</h4>
            <table className="quant-table">
              <thead>
                <tr>
                  <th>1</th>
                  <th>General Information</th>
                  <th>Unit</th>
                  <th>Input</th>
                </tr>
              </thead>
              <tbody>
                {part1.slice(0, 5).map(([num, label, unit, key]) => (
                  <tr key={key}>
                    <td>{num}</td>
                    <td>{label}</td>
                    <td>{unit}</td>
                    <td>
                      <input
                        type="number"
                        value={formData[key]?.[0] || ""}
                        onChange={(e) => handleChange(key, e.target.value, 0)}
                        className="form-input"
                        aria-label={`${label} for 2024-25`}
                      />
                    </td>
                    {/* <td>
                      <input
                        type="number"
                        value={formData[key]?.[1] || ""}
                        onChange={(e) => handleChange(key, e.target.value, 1)}
                        className="form-input"
                        aria-label={`${label} for 2023-24`}
                      />
                    </td> */}
                  </tr>
                ))}
              </tbody>
            </table>

            {/* GA Names row */}
            <h4>GA Names</h4>
            <div
              style={{
                overflowX: numGAs > 1 ? "auto" : "visible",
                maxWidth: numGAs > 1 ? "100%" : "none",
              }}
            >
              <table
                className="quant-table"
                style={{
                  minWidth:
                    numGAs > 1 ? `${900 + (numGAs - 5) * 160}px` : "900px",
                }}
              >
                <thead>
                  <tr>
                    {Array.from({ length: numGAs }, (_, i) => (
                      <th key={i}>{`GA${i + 1}`}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    {Array.from({ length: numGAs }, (_, i) => (
                      <td key={i}>
                        <input
                          type="text"
                          value={formData.gaNames?.[i] || ""}
                          onChange={(e) =>
                            handleChange("gaNames", e.target.value, i)
                          }
                          className="form-input"
                          placeholder={`Enter GA${i + 1} Name`}
                        />
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Buttons: Add More GA + Delete Last GA */}
            <div
              style={{
                marginTop: 12,
                display: "flex",
                gap: 12,
                alignItems: "center",
              }}
            >
              <button
                type="button"
                onClick={addMoreGA}
                className="btn btn-primary"
              >
                Add More GA
              </button>

              {numGAs > 1 && (
                <button
                  type="button"
                  onClick={confirmDeleteLastGA}
                  className="btn btn-danger"
                  title="Delete last added GA"
                >
                  Delete Last GA
                </button>
              )}

              <div style={{ color: "#444", marginLeft: "auto" }}>
                Total GAs: <strong>{numGAs}</strong>
              </div>
            </div>

            {showDeleteModal && (
              <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
                <Card className="w-96 rounded-2xl shadow-2xl">
                  <CardContent className="p-6">
                    <Typography variant="h6" className="mb-2 font-semibold">
                      Confirm Delete
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      className="mb-6"
                    >
                      Are you sure you want to delete GA no.{" "}
                      <strong>{numGAs}</strong> ?
                    </Typography>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-start",
                        marginTop: "10px",
                        gap: "10px",
                      }}
                    >
                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={cancelDeleteLastGA}
                        sx={{
                          textTransform: "none",
                          borderRadius: "50px",
                          px: 3,
                        }}
                      >
                        No
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        onClick={handleDeleteLastGA}
                        sx={{
                          textTransform: "none",
                          borderRadius: "50px",
                          px: 3,
                        }}
                      >
                        Yes
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* GA-specific information */}
            <h4 style={{ marginTop: 18 }}>GA-Specific Information</h4>
            <div
              style={{
                overflowX: numGAs > 1 ? "auto" : "visible",
                maxWidth: numGAs > 1 ? "100%" : "none",
              }}
            >
              <table
                className="quant-table"
                style={{
                  minWidth:
                    numGAs > 1 ? `${700 + (numGAs - 5) * 160}px` : "700px",
                }}
              >
                <thead>
                  <tr>
                    <th>2</th>
                    <th>GA Specific Information</th>
                    <th>Unit</th>
                    {Array.from({ length: numGAs }, (_, i) => (
                      <th key={i}>
                        {`GA${i + 1}`}
                        {formData.gaNames?.[i]
                          ? ` (${formData.gaNames[i]})`
                          : ""}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {part1
                    .slice(5, 12)
                    .map(([num, label, unit, key, isInput]) => (
                      <tr key={key}>
                        <td>{num}</td>
                        <td>{label}</td>
                        <td>{unit}</td>
                        {isInput ? (
                          Array.from({ length: numGAs }, (_, gaIndex) => (
                            <td key={gaIndex}>
                              {key === "baseDate" ? (
                                <input
                                  type="text"
                                  value="2025-03-31"
                                  readOnly
                                  className="form-input"
                                  aria-label="Base Date (static)"
                                />
                              ) : (
                                <input
                                  type={unit === "Date" ? "date" : "number"}
                                  value={formData[key]?.[gaIndex] || ""}
                                  onChange={(e) =>
                                    handleChange(key, e.target.value, gaIndex)
                                  }
                                  className="form-input"
                                  aria-label={`${label} for GA${gaIndex + 1}`}
                                />
                              )}
                            </td>
                          ))
                        ) : (
                          <td colSpan={numGAs}>
                            {calculatedRows.find((row) => row.key === key)
                              ?.value || 0}
                          </td>
                        )}
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            {/* D-PNG Connection */}
            <h4 style={{ marginTop: 18 }}>D-PNG Connection</h4>
            <div
              style={{
                overflowX: numGAs > 1 ? "auto" : "visible",
                maxWidth: numGAs > 1 ? "100%" : "none",
              }}
            >
              <table
                className="quant-table"
                style={{
                  minWidth:
                    numGAs > 1 ? `${700 + (numGAs - 5) * 160}px` : "700px",
                }}
              >
                <thead>
                  <tr>
                    <th>3</th>
                    <th>D-PNG Connection</th>
                    <th>Unit</th>
                    {Array.from({ length: numGAs }, (_, i) => (
                      <React.Fragment key={i}>
                        <th style={{ minWidth: "150px" }}>
                          {`GA${i + 1}`}
                          {formData.gaNames?.[i]
                            ? ` (${formData.gaNames[i]})`
                            : ""}{" "}
                          (2024-25)
                        </th>
                        <th style={{ minWidth: "150px" }}>
                          {`GA${i + 1}`}
                          {formData.gaNames?.[i]
                            ? ` (${formData.gaNames[i]})`
                            : ""}{" "}
                          (2023-24)
                        </th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {part1
                    .slice(13)
                    .map(([num, label, unit, key, isInput], index) => (
                      <tr key={key}>
                        <td>{num}</td>
                        <td style={{ minWidth: "150px" }}>{label}</td>
                        <td>{unit}</td>
                        {isInput ? (
                          Array.from({ length: numGAs }, (_, gaIndex) => (
                            <React.Fragment key={gaIndex}>
                              {index === 0 ? (
                                // Single input for the top key
                                <td colSpan={2}>
                                  <input
                                    type={unit === "Date" ? "date" : "number"}
                                    value={formData[key]?.[gaIndex] || ""}
                                    onChange={(e) =>
                                      handleChange(key, e.target.value, gaIndex)
                                    }
                                    className="form-input"
                                    aria-label={`${label} for GA${gaIndex + 1}`}
                                  />
                                </td>
                              ) : (
                                // Two inputs for the rest
                                <>
                                  <td>
                                    <input
                                      type={unit === "Date" ? "date" : "number"}
                                      value={
                                        formData[key]?.[gaIndex]?.[0] || ""
                                      }
                                      onChange={(e) =>
                                        handleChange(
                                          key,
                                          e.target.value,
                                          gaIndex,
                                          0
                                        )
                                      }
                                      className="form-input"
                                      aria-label={`${label} for GA${
                                        gaIndex + 1
                                      } 2024-25`}
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type={unit === "Date" ? "date" : "number"}
                                      value={
                                        formData[key]?.[gaIndex]?.[1] || ""
                                      }
                                      onChange={(e) =>
                                        handleChange(
                                          key,
                                          e.target.value,
                                          gaIndex,
                                          1
                                        )
                                      }
                                      className="form-input"
                                      aria-label={`${label} for GA${
                                        gaIndex + 1
                                      } 2023-24`}
                                    />
                                  </td>
                                </>
                              )}
                            </React.Fragment>
                          ))
                        ) : (
                          <td colSpan={numGAs * 2}>
                            {calculatedRows.find((row) => row.key === key)
                              ?.value || 0}
                          </td>
                        )}
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {currentStep === 4 && (
          <div>
            <h3 className="step-title">
              Step 4: Quantitative Information Part 2
            </h3>
            <h4>CNG Stations</h4>
            <div
              style={{
                overflowX: numGAs > 1 ? "auto" : "visible",
                maxWidth: numGAs > 1 ? "100%" : "none",
              }}
            >
              <table
                className="quant-table"
                style={{
                  minWidth:
                    numGAs > 1 ? `${700 + (numGAs - 5) * 160}px` : "700px",
                }}
              >
                <thead>
                  <tr>
                    <th>4</th>
                    <th>CNG Stations</th>
                    <th>Unit</th>
                    {Array.from({ length: numGAs }, (_, i) => (
                      <React.Fragment key={i}>
                        <th style={{ minWidth: "150px" }}>{`GA${
                          i + 1
                        } (2024-25)`}</th>
                        <th style={{ minWidth: "150px" }}>{`GA${
                          i + 1
                        } (2023-24)`}</th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {part2
                    .slice(0, 3)
                    .map(([num, label, unit, key, isInput], index) => (
                      <tr key={key}>
                        <td>{num}</td>
                        <td>{label}</td>
                        <td>{unit}</td>
                        {isInput ? (
                          Array.from({ length: numGAs }, (_, gaIndex) => (
                            <React.Fragment key={gaIndex}>
                              {index === 0 ? (
                                // Single input for the top key
                                <td colSpan={2}>
                                  <input
                                    type={unit === "Date" ? "date" : "number"}
                                    value={formData[key]?.[gaIndex] || ""}
                                    onChange={(e) =>
                                      handleChange(key, e.target.value, gaIndex)
                                    }
                                    className="form-input"
                                    aria-label={`${label} for GA${gaIndex + 1}`}
                                  />
                                </td>
                              ) : (
                                // Two inputs for the rest
                                <>
                                  <td>
                                    <input
                                      type={unit === "Date" ? "date" : "number"}
                                      value={
                                        formData[key]?.[gaIndex]?.[0] || ""
                                      }
                                      onChange={(e) =>
                                        handleChange(
                                          key,
                                          e.target.value,
                                          gaIndex,
                                          0
                                        )
                                      }
                                      className="form-input"
                                      aria-label={`${label} for GA${
                                        gaIndex + 1
                                      } 2024-25`}
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type={unit === "Date" ? "date" : "number"}
                                      value={
                                        formData[key]?.[gaIndex]?.[1] || ""
                                      }
                                      onChange={(e) =>
                                        handleChange(
                                          key,
                                          e.target.value,
                                          gaIndex,
                                          1
                                        )
                                      }
                                      className="form-input"
                                      aria-label={`${label} for GA${
                                        gaIndex + 1
                                      } 2023-24`}
                                    />
                                  </td>
                                </>
                              )}
                            </React.Fragment>
                          ))
                        ) : (
                          <td colSpan={numGAs * 2}>
                            {calculatedRows.find((row) => row.key === key)
                              ?.value || 0}
                          </td>
                        )}
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            <h4>Inch-km of Pipeline Laid</h4>
            <div
              style={{
                overflowX: numGAs > 1 ? "auto" : "visible",
                maxWidth: numGAs > 1 ? "100%" : "none",
              }}
            >
              <table
                className="quant-table"
                style={{
                  minWidth:
                    numGAs > 1 ? `${700 + (numGAs - 5) * 160}px` : "700px",
                }}
              >
                <thead>
                  <tr>
                    <th>5</th>
                    <th>Inch-km of Pipeline Laid</th>
                    <th>Unit</th>
                    {Array.from({ length: numGAs }, (_, i) => (
                      <React.Fragment key={i}>
                        <th style={{ minWidth: "150px" }}>{`GA${
                          i + 1
                        } (2024-25)`}</th>
                        <th style={{ minWidth: "150px" }}>{`GA${
                          i + 1
                        } (2023-24)`}</th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {part2
                    .slice(3, 6)
                    .map(([num, label, unit, key, isInput], index) => (
                      <tr key={key}>
                        <td>{num}</td>
                        <td>{label}</td>
                        <td>{unit}</td>
                        {isInput ? (
                          Array.from({ length: numGAs }, (_, gaIndex) => (
                            <React.Fragment key={gaIndex}>
                              {index === 0 ? (
                                // Single input for the top key
                                <td colSpan={2}>
                                  <input
                                    type={unit === "Date" ? "date" : "number"}
                                    value={formData[key]?.[gaIndex] || ""}
                                    onChange={(e) =>
                                      handleChange(key, e.target.value, gaIndex)
                                    }
                                    className="form-input"
                                    aria-label={`${label} for GA${gaIndex + 1}`}
                                  />
                                </td>
                              ) : (
                                // Two inputs for the rest
                                <>
                                  <td>
                                    <input
                                      type={unit === "Date" ? "date" : "number"}
                                      value={
                                        formData[key]?.[gaIndex]?.[0] || ""
                                      }
                                      onChange={(e) =>
                                        handleChange(
                                          key,
                                          e.target.value,
                                          gaIndex,
                                          0
                                        )
                                      }
                                      className="form-input"
                                      aria-label={`${label} for GA${
                                        gaIndex + 1
                                      } 2024-25`}
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type={unit === "Date" ? "date" : "number"}
                                      value={
                                        formData[key]?.[gaIndex]?.[1] || ""
                                      }
                                      onChange={(e) =>
                                        handleChange(
                                          key,
                                          e.target.value,
                                          gaIndex,
                                          1
                                        )
                                      }
                                      className="form-input"
                                      aria-label={`${label} for GA${
                                        gaIndex + 1
                                      } 2023-24`}
                                    />
                                  </td>
                                </>
                              )}
                            </React.Fragment>
                          ))
                        ) : (
                          <td colSpan={numGAs * 2}>
                            {calculatedRows.find((row) => row.key === key)
                              ?.value || 0}
                          </td>
                        )}
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            <h4>Natural Gas Sales & CBG Intake</h4>
            <div
              style={{
                overflowX: numGAs > 1 ? "auto" : "visible",
                maxWidth: numGAs > 1 ? "100%" : "none",
              }}
            >
              <table
                className="quant-table"
                style={{
                  minWidth:
                    numGAs > 1 ? `${700 + (numGAs - 5) * 160}px` : "700px",
                }}
              >
                <thead>
                  <tr>
                    <th>6</th>
                    <th>Natural Gas Sales & CBG Intake</th>
                    <th>Unit</th>
                    {Array.from({ length: numGAs }, (_, i) => (
                      <React.Fragment key={i}>
                        <th style={{ minWidth: "150px" }}>{`GA${
                          i + 1
                        } (2024-25)`}</th>
                        <th style={{ minWidth: "150px" }}>{`GA${
                          i + 1
                        } (2023-24)`}</th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {part2.slice(6, 9).map(([num, label, unit, key]) => (
                    <tr key={key}>
                      <td>{num}</td>
                      <td>{label}</td>
                      <td>{unit}</td>
                      {Array.from({ length: numGAs }, (_, gaIndex) => (
                        <React.Fragment key={gaIndex}>
                          <td>
                            <input
                              type="number"
                              value={formData[key]?.[gaIndex]?.[0] || ""}
                              onChange={(e) =>
                                handleChange(key, e.target.value, gaIndex, 0)
                              }
                              className="form-input"
                              aria-label={`${label} for GA${
                                gaIndex + 1
                              } 2024-25`}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              value={formData[key]?.[gaIndex]?.[1] || ""}
                              onChange={(e) =>
                                handleChange(key, e.target.value, gaIndex, 1)
                              }
                              className="form-input"
                              aria-label={`${label} for GA${
                                gaIndex + 1
                              } 2023-24`}
                            />
                          </td>
                        </React.Fragment>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <h4>Safety</h4>
            <div
              style={{
                overflowX: numGAs > 1 ? "auto" : "visible",
                maxWidth: numGAs > 1 ? "100%" : "none",
              }}
            >
              <table
                className="quant-table"
                style={{
                  minWidth:
                    numGAs > 1 ? `${700 + (numGAs - 5) * 160}px` : "700px",
                }}
              >
                <thead>
                  <tr>
                    <th>7</th>
                    <th>Safety</th>
                    <th>Unit</th>
                    {Array.from({ length: numGAs }, (_, i) => (
                      <React.Fragment key={i}>
                        <th style={{ minWidth: "150px" }}>{`GA${
                          i + 1
                        } (2024-25)`}</th>
                        <th style={{ minWidth: "150px" }}>{`GA${
                          i + 1
                        } (2023-24)`}</th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {part2.slice(9, 15).map(([num, label, unit, key]) => (
                    <tr key={key}>
                      <td>{num}</td>
                      <td>{label}</td>
                      <td>{unit}</td>
                      {Array.from({ length: numGAs }, (_, gaIndex) => (
                        <React.Fragment key={gaIndex}>
                          <td>
                            <input
                              type="number"
                              value={formData[key]?.[gaIndex]?.[0] || ""}
                              onChange={(e) =>
                                handleChange(key, e.target.value, gaIndex, 0)
                              }
                              className="form-input"
                              aria-label={`${label} for GA${
                                gaIndex + 1
                              } 2024-25`}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              value={formData[key]?.[gaIndex]?.[1] || ""}
                              onChange={(e) =>
                                handleChange(key, e.target.value, gaIndex, 1)
                              }
                              className="form-input"
                              aria-label={`${label} for GA${
                                gaIndex + 1
                              } 2023-24`}
                            />
                          </td>
                        </React.Fragment>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="form-group">
              <label htmlFor="comment">Comments</label>
              <textarea
                id="comment"
                name="comment"
                value={formData.comment}
                onChange={(e) => handleChange("comment", e.target.value)}
                onBlur={(e) => handleBlur("comment", e.target.value)}
                className={`form-textarea ${
                  fieldErrors.comment ? "has-error" : ""
                }`}
                // maxLength={COMMENT_MAX_LENGTH}
                placeholder="Comments in (200 words) against input parameter, if any"
                aria-describedby="comment-error"
              />
              {fieldErrors.comment && (
                <span className="error-tooltip" id="comment-error" role="alert">
                  {fieldErrors.comment}
                </span>
              )}
            </div>
          </div>
        )}
        {currentStep === 5 && (
          <div className="form-step">
            <h3 className="step-title">Step 5: Attachments & Declaration</h3>
            <div className="form-group">
              <label>List of Attachments (Optional):</label>
              <table className="quant-table">
                <thead>
                  <tr>
                    <th>S. No.</th>
                    <th>Description</th>
                    <th>Upload (only jpg, png, pdf, max 5 MB each)</th>
                  </tr>
                </thead>
                {/* <tbody>
                  {[1, 2, 3, 4].map((num) => {
                    const key = `attachments${num}`;
                    const attachment = formData[key];
                    return (
                      <tr key={key}>
                        <td>{num}</td>
                        <td>
                          <input
                            type="text"
                            name={`${key}.description`}
                            value={attachment.description}
                            onChange={(e) =>
                              handleAttachmentChange(
                                key,
                                "description",
                                e.target.value
                              )
                            }
                            onBlur={(e) =>
                              handleBlur(`${key}.description`, e.target.value)
                            }
                            placeholder="Enter description"
                            className={`form-input ${
                              fieldErrors[`${key}.description`]
                                ? "has-error"
                                : ""
                            }`}
                            maxLength={FIELD_MAX_LENGTH}
                            aria-describedby={`${key}-description-error`}
                          />
                          {fieldErrors[`${key}.description`] && (
                            <span
                              className="error-tooltip"
                              id={`${key}-description-error`}
                              role="alert"
                            >
                              {fieldErrors[`${key}.description`]}
                            </span>
                          )}
                        </td>
                        <td>
                          <input
                            type="file"
                            accept=".jpg,.png,.pdf"
                            onChange={(e) =>
                              handleAttachmentChange(
                                key,
                                "file",
                                e.target.files[0],
                                e
                              )
                            }
                            className={`form-input mt-4 ${
                              fieldErrors[key] ? "has-error" : ""
                            }`}
                            aria-label={`Upload attachment ${num}`}
                          />
                          {attachment.file && (
                            <p className="file-name">
                              Selected file: {attachment.file.name}
                            </p>
                          )}
                          {fieldErrors[key] && (
                            <span
                              className="error-tooltip"
                              id={`${key}-error`}
                              role="alert"
                            >
                              {fieldErrors[key]}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody> */}
                <tbody>
                  {[1, 2, 3, 4].map((num) => {
                    const key = `attachments${num}`;
                    const attachment = formData[key] || {
                      description: "",
                      file: null,
                    };
                    return (
                      <tr key={key}>
                        <td>{num}</td>
                        <td>
                          <input
                            type="text"
                            name={`${key}.description`}
                            value={attachment.description}
                            onChange={(e) =>
                              handleAttachmentChange(
                                key,
                                "description",
                                e.target.value
                              )
                            }
                            placeholder="Enter description"
                            maxLength={FIELD_MAX_LENGTH}
                            className="form-input"
                          />
                        </td>
                        <td>
                          <input
                            type="file"
                            accept=".jpg,.png,.pdf"
                            onChange={(e) =>
                              handleAttachmentChange(
                                key,
                                "file",
                                e.target.files[0],
                                e
                              )
                            }
                            className="form-input mt-4"
                          />
                          {attachment.file && (
                            <p className="file-name">
                              Selected file: {attachment.file.name}
                            </p>
                          )}
                          {attachment.url && !attachment.file && (
                            <p className="file-name">
                              Uploaded file:{" "}
                              <button
                                type="button"
                                onClick={() => viewFile(attachment.url)}
                                style={{
                                  background: "none",
                                  border: "none",
                                  padding: 0,
                                  color: "#06c",
                                  cursor: "pointer",
                                  textDecoration: "underline",
                                }}
                              >
                                View
                              </button>
                            </p>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="form-group">
              <label>
                Print the completed application form and upload it with the
                signature of the approving authority. Without signature of the
                approving authority, application will not be considered valid.{" "}
                <span className="text-red" aria-hidden="true">
                  *
                </span>
              </label>
              <div className="form-navigation">
                <button
                  type="button"
                  onClick={handlePrint}
                  className="btn btn-outline"
                >
                  Print Preview
                </button>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="approvingAuthoritySignature">
                Upload Document with Approving Authority Signature
                (Director/Board Level)
                <span aria-hidden="true" className="text-red">
                  *
                </span>
                :
              </label>
              <input
                type="file"
                id="approvingAuthoritySignature"
                accept=".jpg,.png,.pdf"
                onChange={(e) => {
                  const files = e.target.files;
                  if (files.length > 0) {
                    const confirmed = window.confirm(
                      "Are you sure the form is approved and signed by approving authority?"
                    );
                    if (!confirmed) {
                      // If user clicks "Cancel", clear the input
                      e.target.value = "";
                      return;
                    }
                    handleApprovingAuthorityChange(files);
                  }
                }}
                className={`form-input mt-4 ${
                  fieldErrors.approvingAuthoritySignature ? "has-error" : ""
                }`}
                aria-describedby="approvingAuthoritySignature-error"
                required
              />
              {formData.approvingAuthoritySignature && (
                <p className="file-name">
                  Selected file: {formData.approvingAuthoritySignature.name}
                </p>
              )}
              {formData.approvingAuthoritySignatureUrl &&
                !formData.approvingAuthoritySignature && (
                  <p className="file-name">
                    Uploaded file:{" "}
                    <button
                      type="button"
                      onClick={() =>
                        viewFile(formData.approvingAuthoritySignatureUrl)
                      }
                      style={{
                        background: "none",
                        border: "none",
                        padding: 0,
                        color: "#06c",
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                    >
                      View
                    </button>
                  </p>
                )}
              {fieldErrors.approvingAuthoritySignature && (
                <span
                  className="error-tooltip"
                  id="approvingAuthoritySignature-error"
                  role="alert"
                >
                  {fieldErrors.approvingAuthoritySignature}
                </span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="declaration">
                <input
                  id="declaration"
                  type="checkbox"
                  name="declaration"
                  checked={formData.declaration}
                  onChange={(e) =>
                    handleChange("declaration", e.target.checked)
                  }
                  className={`form-checkbox ${
                    fieldErrors.declaration ? "has-error" : ""
                  }`}
                  aria-required="true"
                  aria-describedby="declaration-error"
                />
                I declare that the information submitted is true and complete.
              </label>
              {fieldErrors.declaration && (
                <span
                  className="error-tooltip"
                  id="declaration-error"
                  role="alert"
                >
                  {fieldErrors.declaration}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    const middlePosition = window.innerHeight / 2; // middle
    window.scrollTo({ top: middlePosition + 100, behavior: "smooth" });
  }, [currentStep]);
  const navigate = useNavigate();
  return (
    <div className="sidebar-guideline-container">
      <SidebarGuideline
        isOpen={true}
        sidebarItems={[{ id: "Guideline", label: "Guideline" }]}
        activeItem={activeItem}
        setActiveItem={setActiveItem}
        selectedAwardCategory={formData.category}
      />
      <div className="application-form">
        <div className="form-header">
          <h1>{awardTitle}</h1>
          <h6>Step {currentStep} of 5</h6>
        </div>
        {isSubmitted ? (
          <div className="thank-you-message">
            <h2>Thank you for your submission!</h2>
            <p>Your Application has been successfully submitted.</p>
            <button onClick={() => navigate("/#Award%20Categories")}>
              Submit Another Application
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {renderStepContent()}
            {currentStep === 1 && (
              <div className="form-navigation-step1">
                <button
                  type="button"
                  onClick={handleSaveDraft}
                  className="btn btn-outline"
                  disabled={isSubmited()}
                >
                  <Save size={16} /> Save Draft
                </button>
                <button
                  type="button"
                  onClick={nextStep}
                  className="btn btn-primary"
                >
                  Next <ChevronRight size={16} />
                </button>
              </div>
            )}
            {currentStep > 1 && (
              <div className="form-navigation">
                <button
                  type="button"
                  onClick={prevStep}
                  className="btn btn-outline"
                >
                  <ChevronLeft size={16} /> Previous
                </button>
                <button
                  type="button"
                  onClick={handleSaveDraft}
                  className="btn btn-outline"
                  disabled={isSubmited()}
                >
                  <Save size={16} /> Save Draft
                </button>
                {currentStep < 5 && (
                  <button
                    type="button"
                    onClick={nextStep}
                    className="btn btn-primary"
                  >
                    Next <ChevronRight size={16} />
                  </button>
                )}
                {currentStep === 5 && (
                  <button
                    type="button"
                    className="btn btn-success"
                    disabled={isSubmited()}
                    onClick={(e) => {
                      if (!formData.declaration) {
                        return alert(
                          "Please accept the declaration before submitting."
                        );
                      }
                      if (!formData.approvingAuthoritySignature) {
                        return alert(
                          "Please upload the approving authority signature file."
                        );
                      }
                      if (!window.confirm("Are you sure you want to submit?")) {
                        e.preventDefault();
                      }
                      alert("CGD registration submitted successfully!");
                      setIsSubmitted(true);
                      handleSubmit(e, "submit");
                    }}
                  >
                    Submit
                  </button>
                )}
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default RegistrationCGD;
